import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase, TrendingUp, AlertTriangle, Users, BarChart3,
  Plus, RefreshCw, X, Check, Eye,
  Building2, UserCheck, Bell, Activity, Search,
  Scale, Globe, ShieldAlert, Handshake,
  Brain, Swords, FileText, ChevronRight, Zap,
  Radio, BookOpen, Rss, Trash2, BellRing,
  Newspaper, ClipboardList, BarChart2, Target, CheckCircle2, Clock, ListTodo,
  Download
} from 'lucide-react';
import { API_BASE_URL } from '../config';

// ── Types ────────────────────────────────────────────────────────────────────

interface Competitor {
  id: number;
  name: string;
  entity_type: string;
  industry: string | null;
  domain: string | null;
  keywords: string[] | string | null;
  executives: string[] | string | null;
  is_active: boolean;
  created_at: string;
}

interface Mention {
  id: number;
  competitor_id: number;
  title: string | null;
  content_snippet: string | null;
  source: string | null;
  url: string | null;
  sentiment_score: number;
  sentiment_label: string | null;
  reach_estimate: number;
  executive_named: string | null;
  mention_type: string | null;
  detected_at: string;
}

interface LeadershipChange {
  id: number;
  competitor_id: number;
  executive_name: string;
  old_role: string | null;
  new_role: string | null;
  change_type: string | null;
  evidence_snippet: string | null;
  confidence: number;
  status: string;
  detected_at: string;
}

interface BIAlert {
  id: number;
  competitor_id: number;
  alert_type: string;
  severity: string;
  title: string;
  summary: string | null;
  status: string;
  created_at: string;
}

interface Overview {
  total_competitors: number;
  active_competitors: number;
  open_alerts: number;
  critical_alerts: number;
  leadership_changes_7d: number;
  top_mentioned: { name: string; count: number }[];
}

// ── Phase 2 types ─────────────────────────────────────────────────────────────

interface IndustryNarrative {
  id: number;
  title: string;
  keywords: string[];
  sentiment_score: number;
  volume: number;
  source_breakdown: Record<string, number>;
  first_seen: string | null;
  last_seen: string | null;
}

interface RegulatoryAlert {
  id: number;
  industry: string;
  jurisdiction: string | null;
  title: string;
  summary: string | null;
  source_name: string | null;
  source_url: string | null;
  severity: string;
  regulation_type: string;
  status: string;
  evidence_snippet: string | null;
  detected_at: string | null;
}

interface TrendPoint {
  date: string;
  total_volume: number;
  avg_sentiment: number;
  regulatory_events: number;
}

// ── Phase 3 types ─────────────────────────────────────────────────────────────

interface SupplyEntity {
  id: number;
  name: string;
  entity_type: string;
  industry: string | null;
  country: string | null;
  keywords: string[];
  risk_score: number;
  is_active: boolean;
}

interface SupplyRisk {
  id: number;
  entity_id: number | null;
  risk_type: string;
  severity: string;
  title: string;
  description: string | null;
  source_name: string | null;
  source_url: string | null;
  affected_countries: string[];
  status: string;
  detected_at: string | null;
}

interface DarkWebAlert {
  id: number;
  alert_type: string;
  severity: string;
  title: string;
  summary: string | null;
  source_indicator: string | null;
  affected_entity: string | null;
  evidence_snippet: string | null;
  status: string;
  detected_at: string | null;
}

interface MASignal {
  id: number;
  signal_type: string;
  acquirer: string | null;
  target: string | null;
  title: string;
  summary: string | null;
  source_name: string | null;
  source_url: string | null;
  confidence: number;
  deal_value: string | null;
  status: string;
  detected_at: string | null;
}

// ── Phase 4 interfaces ────────────────────────────────────────────────────────

interface RiskForecast {
  id: number;
  industry: string | null;
  entity_name: string | null;
  forecast_type: string;
  horizon_days: number;
  risk_score_now: number;
  risk_score_7d: number | null;
  risk_score_30d: number | null;
  risk_score_90d: number | null;
  trend: string;
  drivers: string | null;
  mitigations: string | null;
  confidence: number;
  data_points_used: number;
  generated_at: string | null;
}

interface PredictiveScenario {
  id: number;
  industry: string | null;
  name: string;
  scenario_type: string;
  description: string | null;
  outcome_score: number;
  probability: number;
  impact_areas: string | null;
  recommendations: string | null;
  status: string;
  created_by: string | null;
  created_at: string | null;
}

interface StrategicReport {
  id: number;
  industry: string | null;
  report_type: string;
  title: string;
  executive_summary: string | null;
  key_findings: string | null;
  risk_highlights: string | null;
  opportunities: string | null;
  recommended_actions: string | null;
  data_sources: string | null;
  period_start: string | null;
  period_end: string | null;
  status: string;
  generated_at: string | null;
}

interface WarGameSession {
  id: number;
  industry: string | null;
  title: string;
  our_company: string | null;
  adversary: string | null;
  game_type: string;
  moves: string | null;
  final_score_us: number;
  final_score_adv: number;
  outcome_summary: string | null;
  lessons_learned: string | null;
  status: string;
  created_at: string | null;
}

// ── Phase 5 interfaces ────────────────────────────────────────────────────────

interface FeedEvent {
  id: number;
  source_phase: string;
  source_type: string;
  source_id: number | null;
  title: string;
  summary: string | null;
  severity: string;
  relevance: number;
  tags: string | null;
  is_read: boolean;
  occurred_at: string | null;
  ingested_at: string | null;
}

interface WatchlistEntry {
  id: number;
  industry: string | null;
  label: string;
  keywords: string | null;
  entity_type: string;
  phases: string | null;
  is_active: boolean;
  hit_count: number;
  last_hit_at: string | null;
  created_at: string | null;
}

interface NotificationRule {
  id: number;
  industry: string | null;
  name: string;
  description: string | null;
  trigger_phase: string | null;
  trigger_type: string | null;
  min_severity: string;
  keywords: string | null;
  action: string;
  action_target: string | null;
  last_fired_at: string | null;
  fire_count: number;
  is_active: boolean;
  created_at: string | null;
}

interface CorrelationSummary {
  industry: string;
  period_days: number;
  overall_risk_level: string;
  combined_risk_score: number;
  signal_counts: Record<string, number>;
  top_supply_risk: string | null;
  top_dark_web: string | null;
  recommendations: string[];
}

// ── Phase 6 Types ─────────────────────────────────────────────────────────────

interface ExecutiveBriefing {
  id: number;
  industry: string | null;
  briefing_type: string;
  title: string;
  risk_headline: string | null;
  overall_risk: string;
  risk_score: number;
  sections: string | null;
  key_metrics: string | null;
  action_items: string | null;
  signal_counts: string | null;
  period_start: string | null;
  period_end: string | null;
  status: string;
  generated_at: string;
}

interface AnalystNote {
  id: number;
  industry: string | null;
  feed_id: number | null;
  author: string | null;
  note: string;
  tags: string | null;
  is_pinned: boolean;
  created_at: string;
}

interface IntelligenceTask {
  id: number;
  industry: string | null;
  feed_id: number | null;
  title: string;
  description: string | null;
  priority: string;
  assignee: string | null;
  due_date: string | null;
  status: string;
  created_by: string | null;
  created_at: string;
}

interface Benchmark {
  id: number;
  industry: string | null;
  benchmark_type: string;
  current_score: number;
  baseline_score: number | null;
  percentile: number | null;
  peer_industries: string | null;
  peer_scores: string | null;
  delta_vs_baseline: number;
  trend: string;
  generated_at: string;
}

// ── API helper ────────────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, opts?: RequestInit): Promise<T> {
  const r = await fetch(`${API_BASE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json() as Promise<T>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const sentimentColor = (score: number) =>
  score > 0.1 ? 'text-emerald-400' : score < -0.1 ? 'text-rose-400' : 'text-zinc-400';

const sentimentBg = (score: number) =>
  score > 0.1
    ? 'bg-emerald-500/10 border-emerald-500/20'
    : score < -0.1
    ? 'bg-rose-500/10 border-rose-500/20'
    : 'bg-zinc-800/40 border-zinc-700/30';

const SEV_COLOR: Record<string, string> = {
  CRITICAL: 'text-rose-400 bg-rose-500/10 border-rose-500/20',
  HIGH:     'text-amber-400 bg-amber-500/10 border-amber-500/20',
  MEDIUM:   'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
  LOW:      'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
};

const safeArr = (v: string[] | string | null | undefined): string[] => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v); } catch { return [String(v)]; }
};

const fmtDate = (s: string) => {
  try {
    const d = new Date(s);
    const diff = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString();
  } catch { return s; }
};

// ── StatCard ──────────────────────────────────────────────────────────────────

function StatCard({
  icon: Icon, label, value, color,
}: {
  icon: React.ElementType; label: string; value: number | string; color: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-panel rounded-2xl p-5 relative overflow-hidden"
    >
      <div className={`absolute -right-4 -top-4 w-20 h-20 ${color}/10 rounded-full blur-2xl`} />
      <div className={`inline-flex p-2.5 rounded-xl mb-3 ${color}/10`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <p className="text-2xl font-bold text-white mb-1">{value}</p>
      <p className="text-sm font-semibold text-zinc-300">{label}</p>
    </motion.div>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview',      label: 'Overview',           icon: BarChart3   },
  { id: 'competitors',   label: 'Competitors',         icon: Building2   },
  { id: 'mentions',      label: 'Mentions',            icon: Activity    },
  { id: 'leadership',    label: 'Leadership Changes',  icon: UserCheck   },
  { id: 'alerts',        label: 'BI Alerts',           icon: Bell        },
  { id: 'market',        label: 'Market Intelligence', icon: TrendingUp  },
  { id: 'regulatory',    label: 'Regulatory Alerts',   icon: Scale       },
  { id: 'supply_risk',   label: 'Supply Chain Risk',   icon: ShieldAlert },
  { id: 'dark_intel',    label: 'Dark Intelligence',   icon: Handshake   },
  { id: 'risk_forecast', label: 'Risk Forecast',        icon: Brain       },
  { id: 'scenario_sim',  label: 'Scenario Simulator',   icon: Swords      },
  { id: 'intel_feed',    label: 'Intelligence Feed',     icon: Rss         },
  { id: 'watchlist',     label: 'Watchlist & Rules',     icon: BellRing    },
  { id: 'exec_briefs',   label: 'Executive Briefs',      icon: Newspaper   },
  { id: 'collab',        label: 'Collaboration',         icon: ListTodo    },
] as const;

type TabId = typeof TABS[number]['id'];

// ── Main component ────────────────────────────────────────────────────────────

export default function BusinessIntelligence() {
  const [tab, setTab] = useState<TabId>('overview');

  // data
  const [overview,     setOverview]     = useState<Overview | null>(null);
  const [competitors,  setCompetitors]  = useState<Competitor[]>([]);
  const [mentions,     setMentions]     = useState<Mention[]>([]);
  const [leadership,   setLeadership]   = useState<LeadershipChange[]>([]);
  const [biAlerts,     setBiAlerts]     = useState<BIAlert[]>([]);

  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState<string | null>(null);

  // create-competitor form
  const [showForm,  setShowForm]  = useState(false);
  const [form,      setForm]      = useState({ name: '', entity_type: 'company', industry: '', keywords: '', executives: '' });
  const [submitting,setSubmitting]= useState(false);

  // scan
  const [scanningId, setScanningId] = useState<number | null>(null);

  // filters
  const [mentionFilter,   setMentionFilter]   = useState('');
  const [alertStatusFilter, setAlertStatus]   = useState<string>('all');

  // ── Phase 2 state ──────────────────────────────────────────────────────────
  const [industries,        setIndustries]       = useState<string[]>([]);
  const [selectedIndustry,  setSelectedIndustry] = useState<string>('');
  const [narratives,        setNarratives]        = useState<IndustryNarrative[]>([]);
  const [regAlerts,         setRegAlerts]         = useState<RegulatoryAlert[]>([]);
  const [trendData,         setTrendData]         = useState<TrendPoint[]>([]);
  const [regSevFilter,      setRegSevFilter]      = useState<string>('all');
  const [regStatusFilter,   setRegStatusFilter]   = useState<string>('all');
  const [phase2Loading,     setPhase2Loading]     = useState(false);

  // ── Phase 3 state ──────────────────────────────────────────────────────────
  const [supplyEntities,   setSupplyEntities]  = useState<SupplyEntity[]>([]);
  const [supplyRisks,      setSupplyRisks]     = useState<SupplyRisk[]>([]);
  const [darkWebAlerts,    setDarkWebAlerts]   = useState<DarkWebAlert[]>([]);
  const [maSignals,        setMASignals]        = useState<MASignal[]>([]);
  const [phase3Loading,    setPhase3Loading]   = useState(false);
  const [scRiskFilter,     setScRiskFilter]    = useState<string>('all');
  const [dwTypeFilter,     setDwTypeFilter]    = useState<string>('all');
  const [maTypeFilter,     setMATypeFilter]    = useState<string>('all');
  // entity creation form
  const [showEntityForm,   setShowEntityForm]  = useState(false);
  const [entityForm,       setEntityForm]      = useState({ name: '', entity_type: 'vendor', industry: '', country: '', keywords: '' });
  const [entitySubmitting, setEntitySubmitting]= useState(false);

  // Phase 4 state
  const [forecasts,       setForecasts]       = useState<RiskForecast[]>([]);
  const [scenarios,       setScenarios]       = useState<PredictiveScenario[]>([]);
  const [reports,         setReports]         = useState<StrategicReport[]>([]);
  const [wargames,        setWargames]        = useState<WarGameSession[]>([]);
  const [phase4Loading,   setPhase4Loading]   = useState(false);
  const [forecastType,    setForecastType]    = useState<string>('supply_chain');
  const [expandedReport,  setExpandedReport]  = useState<number | null>(null);
  const [expandedWG,      setExpandedWG]      = useState<number | null>(null);
  const [scenarioForm,    setScenarioForm]    = useState({
    name: '', scenario_type: 'competitive', description: '',
    threat_level: '0.5', market_share: '0.3', duration_months: '6',
  });
  const [scenarioRunning, setScenarioRunning] = useState(false);
  const [wgForm,          setWGForm]          = useState({
    title: '', our_company: '', adversary: '', game_type: 'competitive_response',
  });
  const [wgCreating,      setWGCreating]      = useState(false);

  // Phase 5 state
  const [feedEvents,      setFeedEvents]      = useState<FeedEvent[]>([]);
  const [watchlistItems,  setWatchlistItems]  = useState<WatchlistEntry[]>([]);
  const [notifRules,      setNotifRules]      = useState<NotificationRule[]>([]);
  const [correlation,     setCorrelation]     = useState<CorrelationSummary | null>(null);
  const [phase5Loading,   setPhase5Loading]   = useState(false);
  const [feedPhaseFilter, setFeedPhaseFilter] = useState<string>('all');
  const [feedSevFilter,   setFeedSevFilter]   = useState<string>('all');
  const [watchForm,       setWatchForm]       = useState({ label: '', keywords: '', entity_type: 'company' });
  const [ruleForm,        setRuleForm]        = useState({
    name: '', trigger_phase: 'any', trigger_type: 'any',
    min_severity: 'MEDIUM', keywords: '', action: 'feed',
  });
  const [watchSubmitting, setWatchSubmitting] = useState(false);
  const [ruleSubmitting,  setRuleSubmitting]  = useState(false);

  // ── Phase 6 state ──────────────────────────────────────────────────────────
  const [execBriefs,       setExecBriefs]       = useState<ExecutiveBriefing[]>([]);
  const [analystNotes,     setAnalystNotes]     = useState<AnalystNote[]>([]);
  const [intelTasks,       setIntelTasks]       = useState<IntelligenceTask[]>([]);
  const [benchmarks,       setBenchmarks]       = useState<Benchmark[]>([]);
  const [expandedBrief,    setExpandedBrief]    = useState<number | null>(null);
  const [phase6Loading,    setPhase6Loading]    = useState(false);
  const [briefGenerating,  setBriefGenerating]  = useState(false);
  const [benchGenerating,  setBenchGenerating]  = useState(false);
  const [noteForm,         setNoteForm]         = useState({ author: '', note: '', tags: '' });
  const [noteSubmitting,   setNoteSubmitting]   = useState(false);
  const [taskForm,         setTaskForm]         = useState({
    title: '', description: '', priority: 'MEDIUM', assignee: '', due_days: '',
  });
  const [taskSubmitting,   setTaskSubmitting]   = useState(false);

  // ── Fetchers ────────────────────────────────────────────────────────────────

  const fetchOverview = useCallback(async () => {
    try {
      const data = await apiFetch<Overview>('/api/bi/overview');
      setOverview(data);
    } catch { /* non-fatal */ }
  }, []);

  const fetchMentions = useCallback(async (comps: Competitor[]) => {
    const all: Mention[] = [];
    for (const c of comps.slice(0, 5)) {
      try {
        const m = await apiFetch<Mention[]>(`/api/bi/competitors/${c.id}/mentions?days=7&limit=20`);
        all.push(...m);
      } catch { /* skip */ }
    }
    all.sort((a, b) => new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime());
    setMentions(all);
  }, []);

  const fetchLeadership = useCallback(async (comps: Competitor[]) => {
    const all: LeadershipChange[] = [];
    for (const c of comps.slice(0, 5)) {
      try {
        const l = await apiFetch<LeadershipChange[]>(`/api/bi/competitors/${c.id}/leadership?days=30`);
        all.push(...l);
      } catch { /* skip */ }
    }
    all.sort((a, b) => new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime());
    setLeadership(all);
  }, []);

  const fetchAlerts = useCallback(async () => {
    const data = await apiFetch<BIAlert[]>('/api/bi/alerts?days=30&limit=50');
    setBiAlerts(data);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchOverview();
      const comps = await apiFetch<Competitor[]>('/api/bi/competitors');
      setCompetitors(comps);
      await fetchAlerts();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [fetchOverview, fetchAlerts]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (tab === 'mentions'  && competitors.length > 0) fetchMentions(competitors);
    if (tab === 'leadership' && competitors.length > 0) fetchLeadership(competitors);
  }, [tab, competitors, fetchMentions, fetchLeadership]);

  // ── Phase 2 fetchers ────────────────────────────────────────────────────────

  const fetchIndustries = useCallback(async () => {
    try {
      const data = await apiFetch<string[]>('/api/bi/market/industries');
      setIndustries(data);
      if (data.length > 0 && !selectedIndustry) setSelectedIndustry(data[0]);
    } catch { /* non-fatal */ }
  }, [selectedIndustry]);

  const fetchMarketData = useCallback(async (industry: string) => {
    if (!industry) return;
    setPhase2Loading(true);
    try {
      const [narr, reg, trend] = await Promise.all([
        apiFetch<IndustryNarrative[]>(`/api/bi/market/${encodeURIComponent(industry)}/narratives?days=7&limit=30`),
        apiFetch<RegulatoryAlert[]>(`/api/bi/market/${encodeURIComponent(industry)}/regulatory?days=30&limit=50`),
        apiFetch<TrendPoint[]>(`/api/bi/market/${encodeURIComponent(industry)}/trend?days=30`),
      ]);
      setNarratives(narr);
      setRegAlerts(reg);
      setTrendData(trend);
    } catch { /* non-fatal */ } finally {
      setPhase2Loading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'market' || tab === 'regulatory') {
      fetchIndustries();
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedIndustry && (tab === 'market' || tab === 'regulatory')) {
      fetchMarketData(selectedIndustry);
    }
  }, [selectedIndustry, tab, fetchMarketData]);

  const handleRegStatus = async (id: number, status: string) => {
    try {
      await apiFetch(`/api/bi/market/regulatory/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
      setRegAlerts(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    } catch { /* ignore */ }
  };

  // ── Phase 3 fetchers ────────────────────────────────────────────────────────

  const fetchPhase3Data = useCallback(async (industry: string) => {
    if (!industry) return;
    setPhase3Loading(true);
    try {
      const [entities, risks, dw, ma] = await Promise.all([
        apiFetch<SupplyEntity[]>(`/api/bi/supply/entities?industry=${encodeURIComponent(industry)}`),
        apiFetch<SupplyRisk[]>(`/api/bi/supply/risks/${encodeURIComponent(industry)}?days=14&limit=50`),
        apiFetch<DarkWebAlert[]>(`/api/bi/supply/darkweb/${encodeURIComponent(industry)}?days=30&limit=50`),
        apiFetch<MASignal[]>(`/api/bi/supply/ma/${encodeURIComponent(industry)}?days=30&limit=50`),
      ]);
      setSupplyEntities(entities);
      setSupplyRisks(risks);
      setDarkWebAlerts(dw);
      setMASignals(ma);
    } catch { /* non-fatal */ } finally {
      setPhase3Loading(false);
    }
  }, []);

  useEffect(() => {
    if ((tab === 'supply_risk' || tab === 'dark_intel') && selectedIndustry) {
      fetchPhase3Data(selectedIndustry);
    }
  }, [tab, selectedIndustry, fetchPhase3Data]);

  const fetchPhase4Data = useCallback(async (industry: string) => {
    setPhase4Loading(true);
    try {
      const [fcs, scns, rpts, wgs] = await Promise.all([
        apiFetch<RiskForecast[]>(`/api/bi/predict/forecast/${encodeURIComponent(industry)}?limit=10`),
        apiFetch<PredictiveScenario[]>(`/api/bi/predict/scenario/${encodeURIComponent(industry)}?limit=10`),
        apiFetch<StrategicReport[]>(`/api/bi/predict/report/${encodeURIComponent(industry)}?limit=6`),
        apiFetch<WarGameSession[]>(`/api/bi/predict/wargame/${encodeURIComponent(industry)}?limit=6`),
      ]);
      setForecasts(fcs);
      setScenarios(scns);
      setReports(rpts);
      setWargames(wgs);
    } catch { /* non-critical */ }
    finally { setPhase4Loading(false); }
  }, []);

  useEffect(() => {
    if ((tab === 'risk_forecast' || tab === 'scenario_sim') && selectedIndustry) {
      fetchPhase4Data(selectedIndustry);
    }
  }, [tab, selectedIndustry, fetchPhase4Data]);

  const handleRunScenario = async () => {
    if (!scenarioForm.name.trim() || !selectedIndustry) return;
    setScenarioRunning(true);
    try {
      const s = await apiFetch<PredictiveScenario>('/api/bi/predict/scenario', {
        method: 'POST',
        body: JSON.stringify({
          industry: selectedIndustry,
          name: scenarioForm.name.trim(),
          scenario_type: scenarioForm.scenario_type,
          description: scenarioForm.description,
          parameters: {
            threat_level: parseFloat(scenarioForm.threat_level),
            market_share_at_risk: parseFloat(scenarioForm.market_share),
            duration_months: parseFloat(scenarioForm.duration_months),
          },
        }),
      });
      setScenarios(prev => [s, ...prev]);
      setScenarioForm({ name: '', scenario_type: 'competitive', description: '', threat_level: '0.5', market_share: '0.3', duration_months: '6' });
    } catch { /* non-critical */ }
    finally { setScenarioRunning(false); }
  };

  const handleGenerateForecast = async () => {
    if (!selectedIndustry) return;
    setPhase4Loading(true);
    try {
      const f = await apiFetch<RiskForecast>('/api/bi/predict/forecast', {
        method: 'POST',
        body: JSON.stringify({ industry: selectedIndustry, forecast_type: forecastType }),
      });
      setForecasts(prev => [f, ...prev]);
    } catch { /* non-critical */ }
    finally { setPhase4Loading(false); }
  };

  const handleGenerateReport = async (reportType: string) => {
    if (!selectedIndustry) return;
    setPhase4Loading(true);
    try {
      const r = await apiFetch<StrategicReport>('/api/bi/predict/report', {
        method: 'POST',
        body: JSON.stringify({ industry: selectedIndustry, report_type: reportType, days: 7 }),
      });
      setReports(prev => [r, ...prev]);
    } catch { /* non-critical */ }
    finally { setPhase4Loading(false); }
  };

  const handleCreateWargame = async () => {
    if (!wgForm.title.trim() || !wgForm.our_company.trim() || !wgForm.adversary.trim() || !selectedIndustry) return;
    setWGCreating(true);
    try {
      const wg = await apiFetch<WarGameSession>('/api/bi/predict/wargame', {
        method: 'POST',
        body: JSON.stringify({ industry: selectedIndustry, ...wgForm }),
      });
      setWargames(prev => [wg, ...prev]);
      setWGForm({ title: '', our_company: '', adversary: '', game_type: 'competitive_response' });
    } catch { /* non-critical */ }
    finally { setWGCreating(false); }
  };

  const fetchPhase5Data = useCallback(async (industry: string) => {
    setPhase5Loading(true);
    try {
      // Build feed first, then fetch it
      await apiFetch(`/api/bi/fusion/build/${encodeURIComponent(industry)}?days=14`, { method: 'POST' }).catch(() => null);
      const [feed, wl, rules, corr] = await Promise.all([
        apiFetch<FeedEvent[]>(`/api/bi/fusion/feed/${encodeURIComponent(industry)}?days=14&limit=60`),
        apiFetch<WatchlistEntry[]>(`/api/bi/fusion/watchlist/${encodeURIComponent(industry)}`),
        apiFetch<NotificationRule[]>(`/api/bi/fusion/rules/${encodeURIComponent(industry)}`),
        apiFetch<CorrelationSummary>(`/api/bi/fusion/correlate/${encodeURIComponent(industry)}?days=14`),
      ]);
      setFeedEvents(feed);
      setWatchlistItems(wl);
      setNotifRules(rules);
      setCorrelation(corr);
    } catch { /* non-critical */ }
    finally { setPhase5Loading(false); }
  }, []);

  useEffect(() => {
    if ((tab === 'intel_feed' || tab === 'watchlist') && selectedIndustry) {
      fetchPhase5Data(selectedIndustry);
    }
  }, [tab, selectedIndustry, fetchPhase5Data]);

  // ── Phase 6 fetchers ────────────────────────────────────────────────────────

  const fetchPhase6Data = useCallback(async (industry: string) => {
    setPhase6Loading(true);
    try {
      const [briefs, notes, tasks, bench] = await Promise.all([
        apiFetch<ExecutiveBriefing[]>(`/api/bi/executive/briefing/${encodeURIComponent(industry)}?limit=20`),
        apiFetch<AnalystNote[]>(`/api/bi/executive/notes/${encodeURIComponent(industry)}?limit=50`),
        apiFetch<IntelligenceTask[]>(`/api/bi/executive/tasks/${encodeURIComponent(industry)}?limit=50`),
        apiFetch<Benchmark[]>(`/api/bi/executive/benchmark/${encodeURIComponent(industry)}?limit=10`),
      ]);
      setExecBriefs(briefs);
      setAnalystNotes(notes);
      setIntelTasks(tasks);
      setBenchmarks(bench);
    } catch { /* non-critical */ }
    finally { setPhase6Loading(false); }
  }, []);

  useEffect(() => {
    if ((tab === 'exec_briefs' || tab === 'collab') && selectedIndustry) {
      fetchPhase6Data(selectedIndustry);
    }
  }, [tab, selectedIndustry, fetchPhase6Data]);

  const handleGenerateBrief = async (type: string) => {
    if (!selectedIndustry) return;
    setBriefGenerating(true);
    try {
      const brief = await apiFetch<ExecutiveBriefing>('/api/bi/executive/briefing', {
        method: 'POST',
        body: JSON.stringify({ industry: selectedIndustry, briefing_type: type, days: type === 'weekly' ? 7 : type === 'monthly' ? 30 : 1 }),
      });
      setExecBriefs(prev => [brief, ...prev]);
    } catch { /* non-critical */ }
    finally { setBriefGenerating(false); }
  };

  const handleGenerateBenchmark = async () => {
    if (!selectedIndustry) return;
    setBenchGenerating(true);
    try {
      const b = await apiFetch<Benchmark>('/api/bi/executive/benchmark', {
        method: 'POST',
        body: JSON.stringify({ industry: selectedIndustry, benchmark_type: 'overall' }),
      });
      setBenchmarks(prev => [b, ...prev]);
    } catch { /* non-critical */ }
    finally { setBenchGenerating(false); }
  };

  const handleAddNote = async () => {
    if (!noteForm.note.trim() || !noteForm.author.trim() || !selectedIndustry) return;
    setNoteSubmitting(true);
    try {
      const n = await apiFetch<AnalystNote>('/api/bi/executive/notes', {
        method: 'POST',
        body: JSON.stringify({
          industry: selectedIndustry,
          author: noteForm.author.trim(),
          note: noteForm.note.trim(),
          tags: noteForm.tags.split(',').map(t => t.trim()).filter(Boolean),
        }),
      });
      setAnalystNotes(prev => [n, ...prev]);
      setNoteForm({ author: '', note: '', tags: '' });
    } catch { /* non-critical */ }
    finally { setNoteSubmitting(false); }
  };

  const handleDeleteNote = async (id: number) => {
    try {
      await apiFetch(`/api/bi/executive/notes/${id}`, { method: 'DELETE' });
      setAnalystNotes(prev => prev.filter(n => n.id !== id));
    } catch { /* non-critical */ }
  };

  const handleAddTask = async () => {
    if (!taskForm.title.trim() || !selectedIndustry) return;
    setTaskSubmitting(true);
    try {
      const t = await apiFetch<IntelligenceTask>('/api/bi/executive/tasks', {
        method: 'POST',
        body: JSON.stringify({
          industry: selectedIndustry,
          title: taskForm.title.trim(),
          description: taskForm.description.trim() || undefined,
          priority: taskForm.priority,
          assignee: taskForm.assignee.trim() || undefined,
          due_days: taskForm.due_days ? parseInt(taskForm.due_days) : undefined,
        }),
      });
      setIntelTasks(prev => [t, ...prev]);
      setTaskForm({ title: '', description: '', priority: 'MEDIUM', assignee: '', due_days: '' });
    } catch { /* non-critical */ }
    finally { setTaskSubmitting(false); }
  };

  const handleTaskStatus = async (id: number, status: string) => {
    try {
      const t = await apiFetch<IntelligenceTask>(`/api/bi/executive/tasks/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
      setIntelTasks(prev => prev.map(x => x.id === id ? t : x));
    } catch { /* non-critical */ }
  };


  const handleAddWatchlist = async () => {
    if (!watchForm.label.trim() || !selectedIndustry) return;
    setWatchSubmitting(true);
    try {
      const entry = await apiFetch<WatchlistEntry>('/api/bi/fusion/watchlist', {
        method: 'POST',
        body: JSON.stringify({
          industry: selectedIndustry,
          label: watchForm.label.trim(),
          keywords: watchForm.keywords.split(',').map(k => k.trim()).filter(Boolean),
          entity_type: watchForm.entity_type,
        }),
      });
      setWatchlistItems(prev => [entry, ...prev]);
      setWatchForm({ label: '', keywords: '', entity_type: 'company' });
    } catch { /* non-critical */ }
    finally { setWatchSubmitting(false); }
  };

  const handleRemoveWatchlist = async (id: number) => {
    try {
      await apiFetch(`/api/bi/fusion/watchlist/${id}`, { method: 'DELETE' });
      setWatchlistItems(prev => prev.filter(e => e.id !== id));
    } catch { /* non-critical */ }
  };

  const handleAddRule = async () => {
    if (!ruleForm.name.trim() || !selectedIndustry) return;
    setRuleSubmitting(true);
    try {
      const rule = await apiFetch<NotificationRule>('/api/bi/fusion/rules', {
        method: 'POST',
        body: JSON.stringify({
          industry: selectedIndustry,
          name: ruleForm.name.trim(),
          trigger_phase: ruleForm.trigger_phase,
          trigger_type: ruleForm.trigger_type,
          min_severity: ruleForm.min_severity,
          keywords: ruleForm.keywords.split(',').map(k => k.trim()).filter(Boolean),
          action: ruleForm.action,
        }),
      });
      setNotifRules(prev => [rule, ...prev]);
      setRuleForm({ name: '', trigger_phase: 'any', trigger_type: 'any', min_severity: 'MEDIUM', keywords: '', action: 'feed' });
    } catch { /* non-critical */ }
    finally { setRuleSubmitting(false); }
  };

  const handleRemoveRule = async (id: number) => {
    try {
      await apiFetch(`/api/bi/fusion/rules/${id}`, { method: 'DELETE' });
      setNotifRules(prev => prev.filter(r => r.id !== id));
    } catch { /* non-critical */ }
  };

  const handleMarkRead = async (id: number) => {
    try {
      await apiFetch('/api/bi/fusion/feed/read', { method: 'PUT', body: JSON.stringify({ feed_ids: [id] }) });
      setFeedEvents(prev => prev.map(e => e.id === id ? { ...e, is_read: true } : e));
    } catch { /* non-critical */ }
  };

  const handleDwStatus = async (id: number, status: string) => {
    try {
      await apiFetch(`/api/bi/supply/darkweb/${id}/status`, {
        method: 'PUT', body: JSON.stringify({ status }),
      });
      setDarkWebAlerts(prev => prev.map(a => a.id === id ? { ...a, status } : a));
    } catch { /* ignore */ }
  };

  const handleRiskStatus = async (id: number, status: string) => {
    try {
      await apiFetch(`/api/bi/supply/risks/${id}/status`, {
        method: 'PUT', body: JSON.stringify({ status }),
      });
      setSupplyRisks(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    } catch { /* ignore */ }
  };

  const handleMAStatus = async (id: number, status: string) => {
    try {
      await apiFetch(`/api/bi/supply/ma/${id}/status`, {
        method: 'PUT', body: JSON.stringify({ status }),
      });
      setMASignals(prev => prev.map(s => s.id === id ? { ...s, status } : s));
    } catch { /* ignore */ }
  };

  const handleEntityCreate = async () => {
    if (!entityForm.name.trim()) return;
    setEntitySubmitting(true);
    try {
      await apiFetch('/api/bi/supply/entities', {
        method: 'POST',
        body: JSON.stringify({
          name: entityForm.name.trim(),
          entity_type: entityForm.entity_type,
          industry: entityForm.industry || selectedIndustry || null,
          country: entityForm.country || null,
          keywords: entityForm.keywords ? entityForm.keywords.split(',').map(k => k.trim()).filter(Boolean) : [],
        }),
      });
      setEntityForm({ name: '', entity_type: 'vendor', industry: '', country: '', keywords: '' });
      setShowEntityForm(false);
      if (selectedIndustry) fetchPhase3Data(selectedIndustry);
    } catch { /* ignore */ } finally {
      setEntitySubmitting(false);
    }
  };



  // ── Actions ─────────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      const body = {
        name:        form.name.trim(),
        entity_type: form.entity_type,
        industry:    form.industry || null,
        keywords:    form.keywords  ? form.keywords.split(',').map(k => k.trim()).filter(Boolean)  : [],
        executives:  form.executives ? form.executives.split(',').map(e => e.trim()).filter(Boolean) : [],
      };
      await apiFetch('/api/bi/competitors', { method: 'POST', body: JSON.stringify(body) });
      setForm({ name: '', entity_type: 'company', industry: '', keywords: '', executives: '' });
      setShowForm(false);
      await loadAll();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleScan = async (id: number) => {
    setScanningId(id);
    try {
      await apiFetch(`/api/bi/competitors/${id}/scan?hours=24`, { method: 'POST' });
      await fetchMentions(competitors);
      await fetchAlerts();
      await fetchOverview();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Scan failed');
    } finally {
      setScanningId(null);
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      await apiFetch(`/api/bi/competitors/${id}`, { method: 'DELETE' });
      setCompetitors(cs => cs.filter(c => c.id !== id));
      await fetchOverview();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Remove failed');
    }
  };

  const handleLeadershipStatus = async (id: number, status: string) => {
    try {
      await apiFetch(`/api/bi/leadership/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
      setLeadership(ls => ls.map(l => l.id === id ? { ...l, status } : l));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  };

  const handleAlertStatus = async (id: number, status: string) => {
    try {
      await apiFetch(`/api/bi/alerts/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
      setBiAlerts(as => as.map(a => a.id === id ? { ...a, status } : a));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────────

  const filteredMentions = mentions.filter(m =>
    !mentionFilter ||
    m.content_snippet?.toLowerCase().includes(mentionFilter.toLowerCase()) ||
    m.title?.toLowerCase().includes(mentionFilter.toLowerCase())
  );

  const filteredAlerts = biAlerts.filter(a =>
    alertStatusFilter === 'all' || a.status === alertStatusFilter
  );

  const competitorName = (id: number) => competitors.find(c => c.id === id)?.name ?? `#${id}`;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="pb-10">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white mb-1 flex items-center gap-3">
            <Briefcase className="w-7 h-7 text-cyan-400" />
            Business Intelligence
          </h1>
          <p className="text-zinc-400 text-sm">Competitor &amp; Executive Monitoring — Phase 1</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.open(`${API_BASE_URL}/api/export/bi.csv?limit=5000`, '_blank')}
            className="flex items-center gap-2 px-3 py-2 bg-emerald-900/40 hover:bg-emerald-800/50 text-emerald-400 rounded-lg text-sm transition-colors border border-emerald-700/40"
            title="Export BI intelligence feed as CSV"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
          <button
            onClick={() => window.open(`${API_BASE_URL}/api/export/bi.json?limit=5000`, '_blank')}
            className="flex items-center gap-2 px-3 py-2 bg-blue-900/40 hover:bg-blue-800/50 text-blue-400 rounded-lg text-sm transition-colors border border-blue-700/40"
            title="Export BI intelligence feed as JSON"
          >
            <Download className="w-4 h-4" /> JSON
          </button>
          <button
            onClick={loadAll}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-800/60 border border-zinc-700/50 text-zinc-300 hover:text-white hover:border-zinc-500 transition-all text-sm font-medium disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error banner */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mb-4 px-4 py-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm flex items-center justify-between"
          >
            <span>{error}</span>
            <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 bg-zinc-900/60 border border-zinc-800/50 rounded-2xl p-1.5 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
                active
                  ? 'bg-gradient-to-r from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 text-cyan-300'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: OVERVIEW
         ══════════════════════════════════════════════════════════════════════ */}
      {tab === 'overview' && (
        <motion.div key="overview" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard icon={Building2}     label="Total Tracked"     value={overview?.total_competitors ?? 0}     color="text-cyan-400"   />
            <StatCard icon={Bell}          label="Open Alerts"       value={overview?.open_alerts ?? 0}           color="text-amber-400"  />
            <StatCard icon={AlertTriangle} label="Critical Alerts"   value={overview?.critical_alerts ?? 0}       color="text-rose-400"   />
            <StatCard icon={UserCheck}     label="Leadership Events" value={overview?.leadership_changes_7d ?? 0} color="text-purple-400" />
          </div>

          {/* Top mentioned */}
          <div className="glass-panel rounded-2xl p-5 mb-4">
            <h3 className="text-sm font-bold text-zinc-300 uppercase tracking-wider mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-cyan-400" /> Top Mentioned Competitors (7d)
            </h3>
            {overview?.top_mentioned && overview.top_mentioned.length > 0 ? (
              <div className="space-y-3">
                {overview.top_mentioned.map((tm, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs font-mono text-zinc-400 w-5">{i + 1}</span>
                    <span className="text-sm font-medium text-zinc-200 flex-1">{tm.name}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"
                          style={{ width: `${Math.min(100, (tm.count / (overview.top_mentioned[0]?.count || 1)) * 100)}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono text-cyan-400 w-8 text-right">{tm.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-400 text-sm">No mention data yet. Add competitors and run a scan.</p>
            )}
          </div>

          {/* Recent open alerts */}
          {biAlerts.filter(a => a.status === 'open').length > 0 && (
            <div className="glass-panel rounded-2xl p-5">
              <h3 className="text-sm font-bold text-zinc-300 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Bell className="w-4 h-4 text-amber-400" /> Recent Open Alerts
              </h3>
              <div className="space-y-2">
                {biAlerts.filter(a => a.status === 'open').slice(0, 5).map(a => (
                  <div key={a.id} className={`flex items-start gap-3 p-3 rounded-xl border ${SEV_COLOR[a.severity] ?? SEV_COLOR.MEDIUM}`}>
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{a.title}</p>
                      <p className="text-xs opacity-70 mt-0.5">{competitorName(a.competitor_id)} · {fmtDate(a.created_at)}</p>
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider opacity-80">{a.severity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: COMPETITORS
         ══════════════════════════════════════════════════════════════════════ */}
      {tab === 'competitors' && (
        <motion.div key="competitors" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-zinc-400">{competitors.length} competitor{competitors.length !== 1 ? 's' : ''} tracked</p>
            <button
              onClick={() => setShowForm(v => !v)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-all text-sm font-semibold"
            >
              <Plus className="w-4 h-4" />
              Add Competitor
            </button>
          </div>

          {/* Create form */}
          <AnimatePresence>
            {showForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden mb-4"
              >
                <div className="glass-panel rounded-2xl p-5 border border-cyan-500/20">
                  <h3 className="text-sm font-bold text-zinc-300 mb-4">New Competitor / Entity</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-zinc-400 mb-1 block">Name *</label>
                      <input
                        value={form.name}
                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="e.g. Google"
                        className="w-full bg-zinc-800 border border-zinc-600 rounded-xl px-3 py-2 text-sm text-white placeholder:text-zinc-400 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/20"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 mb-1 block">Type</label>
                      <select
                        value={form.entity_type}
                        onChange={e => setForm(f => ({ ...f, entity_type: e.target.value }))}
                        className="w-full bg-zinc-900/60 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-cyan-500/50"
                      >
                        {['company', 'executive', 'product', 'industry'].map(t => (
                          <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 mb-1 block">Industry</label>
                      <input
                        value={form.industry}
                        onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
                        placeholder="e.g. Technology"
                        className="w-full bg-zinc-800 border border-zinc-600 rounded-xl px-3 py-2 text-sm text-white placeholder:text-zinc-400 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/20"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 mb-1 block">Keywords (comma-separated)</label>
                      <input
                        value={form.keywords}
                        onChange={e => setForm(f => ({ ...f, keywords: e.target.value }))}
                        placeholder="e.g. Google, Alphabet, DeepMind"
                        className="w-full bg-zinc-800 border border-zinc-600 rounded-xl px-3 py-2 text-sm text-white placeholder:text-zinc-400 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/20"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs text-zinc-400 mb-1 block">Key Executives (comma-separated)</label>
                      <input
                        value={form.executives}
                        onChange={e => setForm(f => ({ ...f, executives: e.target.value }))}
                        placeholder="e.g. Sundar Pichai, Ruth Porat"
                        className="w-full bg-zinc-800 border border-zinc-600 rounded-xl px-3 py-2 text-sm text-white placeholder:text-zinc-400 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/20"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={handleCreate}
                      disabled={submitting || !form.name.trim()}
                      className="px-5 py-2 rounded-xl bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 font-semibold text-sm hover:bg-cyan-500/30 transition-all disabled:opacity-40"
                    >
                      {submitting ? 'Creating…' : 'Create'}
                    </button>
                    <button
                      onClick={() => setShowForm(false)}
                      className="px-4 py-2 rounded-xl bg-zinc-800 border border-zinc-700 text-zinc-400 text-sm hover:text-white transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {competitors.length === 0 ? (
            <div className="glass-panel rounded-2xl p-10 text-center">
              <Building2 className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
              <p className="text-zinc-400 font-medium">No competitors tracked yet</p>
              <p className="text-zinc-400 text-sm mt-1">Click "Add Competitor" to start monitoring</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {competitors.map(c => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-panel rounded-2xl p-5 border border-zinc-800/50 hover:border-zinc-600/50 transition-all"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h4 className="font-bold text-white text-base">{c.name}</h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                          {c.entity_type}
                        </span>
                        {c.industry && <span className="text-xs text-zinc-400">{c.industry}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleScan(c.id)}
                        disabled={scanningId === c.id}
                        title="Run 24h scan"
                        className="p-1.5 rounded-lg bg-zinc-800 hover:bg-cyan-500/10 text-zinc-400 hover:text-cyan-400 border border-zinc-700 hover:border-cyan-500/30 transition-all disabled:opacity-40"
                      >
                        <Search className={`w-3.5 h-3.5 ${scanningId === c.id ? 'animate-pulse' : ''}`} />
                      </button>
                      <button
                        onClick={() => handleDeactivate(c.id)}
                        title="Remove"
                        className="p-1.5 rounded-lg bg-zinc-800 hover:bg-rose-500/10 text-zinc-400 hover:text-rose-400 border border-zinc-700 hover:border-rose-500/30 transition-all"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {safeArr(c.keywords).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {safeArr(c.keywords).slice(0, 6).map((kw, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-400">
                          {kw}
                        </span>
                      ))}
                    </div>
                  )}

                  {safeArr(c.executives).length > 0 && (
                    <div className="flex items-center gap-1 text-xs text-zinc-400 mt-1">
                      <Users className="w-3 h-3" />
                      {safeArr(c.executives).slice(0, 3).join(', ')}
                    </div>
                  )}

                  <p className="text-xs text-zinc-400 mt-2">Added {fmtDate(c.created_at)}</p>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: MENTIONS
         ══════════════════════════════════════════════════════════════════════ */}
      {tab === 'mentions' && (
        <motion.div key="mentions" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                value={mentionFilter}
                onChange={e => setMentionFilter(e.target.value)}
                placeholder="Filter mentions…"
                className="w-full bg-zinc-900/60 border border-zinc-700 rounded-xl pl-9 pr-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-cyan-500/50"
              />
            </div>
            <button
              onClick={() => fetchMentions(competitors)}
              className="p-2 rounded-xl bg-zinc-800 border border-zinc-700 text-zinc-400 hover:text-white transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {filteredMentions.length === 0 ? (
            <div className="glass-panel rounded-2xl p-10 text-center">
              <Activity className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
              <p className="text-zinc-400 font-medium">No mentions found</p>
              <p className="text-zinc-400 text-sm mt-1">Scan your competitors to collect mentions</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMentions.slice(0, 50).map(m => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`glass-panel rounded-xl p-4 border ${sentimentBg(m.sentiment_score)}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {m.title && <p className="font-semibold text-sm text-zinc-100 mb-1 truncate">{m.title}</p>}
                      {m.content_snippet && (
                        <p className="text-xs text-zinc-400 line-clamp-2">{m.content_snippet}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className="text-xs text-zinc-400">{competitorName(m.competitor_id)}</span>
                        {m.source && (
                          <span className="text-xs text-zinc-400 bg-zinc-800 px-1.5 py-0.5 rounded">{m.source}</span>
                        )}
                        {m.executive_named && (
                          <span className="text-xs text-purple-400 flex items-center gap-1">
                            <UserCheck className="w-3 h-3" />{m.executive_named}
                          </span>
                        )}
                        <span className="text-xs text-zinc-400">{fmtDate(m.detected_at)}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className={`text-sm font-bold font-mono ${sentimentColor(m.sentiment_score)}`}>
                        {m.sentiment_score >= 0 ? '+' : ''}{m.sentiment_score.toFixed(2)}
                      </span>
                      <p className="text-xs text-zinc-400 mt-0.5">{m.sentiment_label ?? 'neutral'}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: LEADERSHIP CHANGES
         ══════════════════════════════════════════════════════════════════════ */}
      {tab === 'leadership' && (
        <motion.div key="leadership" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-zinc-400">{leadership.length} detected event{leadership.length !== 1 ? 's' : ''}</p>
            <button
              onClick={() => fetchLeadership(competitors)}
              className="p-2 rounded-xl bg-zinc-800 border border-zinc-700 text-zinc-400 hover:text-white transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {leadership.length === 0 ? (
            <div className="glass-panel rounded-2xl p-10 text-center">
              <UserCheck className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
              <p className="text-zinc-400 font-medium">No leadership changes detected</p>
              <p className="text-zinc-400 text-sm mt-1">Run scans with executive names configured</p>
            </div>
          ) : (
            <div className="space-y-3">
              {leadership.map(l => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-panel rounded-xl p-4 border border-zinc-800/50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-white text-sm">{l.executive_name}</span>
                        {l.change_type && (
                          <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">
                            {l.change_type}
                          </span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded border ${
                          l.status === 'confirmed'  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                          : l.status === 'dismissed' ? 'bg-zinc-700/50 text-zinc-400 border-zinc-700'
                          : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                        }`}>{l.status}</span>
                      </div>
                      {l.evidence_snippet && (
                        <p className="text-xs text-zinc-400 italic mb-1 line-clamp-2">"{l.evidence_snippet}"</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-zinc-400">
                        <span>{competitorName(l.competitor_id)}</span>
                        <span>confidence: {(l.confidence * 100).toFixed(0)}%</span>
                        <span>{fmtDate(l.detected_at)}</span>
                      </div>
                    </div>
                    {l.status === 'new' && (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => handleLeadershipStatus(l.id, 'confirmed')}
                          className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all"
                          title="Confirm"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleLeadershipStatus(l.id, 'dismissed')}
                          className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 border border-zinc-700 hover:bg-zinc-700 transition-all"
                          title="Dismiss"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: BI ALERTS
         ══════════════════════════════════════════════════════════════════════ */}
      {tab === 'alerts' && (
        <motion.div key="alerts" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex gap-1 bg-zinc-900/60 border border-zinc-800 rounded-xl p-1">
              {['all', 'open', 'acknowledged', 'dismissed'].map(s => (
                <button
                  key={s}
                  onClick={() => setAlertStatus(s)}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                    alertStatusFilter === s
                      ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                      : 'text-zinc-400 hover:text-zinc-300'
                  }`}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <span className="text-xs text-zinc-400 ml-auto">
              {filteredAlerts.length} alert{filteredAlerts.length !== 1 ? 's' : ''}
            </span>
          </div>

          {filteredAlerts.length === 0 ? (
            <div className="glass-panel rounded-2xl p-10 text-center">
              <Bell className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
              <p className="text-zinc-400 font-medium">No BI alerts</p>
              <p className="text-zinc-400 text-sm mt-1">Alerts are generated automatically when scanning</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAlerts.map(a => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`glass-panel rounded-xl border ${SEV_COLOR[a.severity] ?? SEV_COLOR.MEDIUM} p-4`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-bold uppercase tracking-wider">{a.severity}</span>
                        <span className="text-xs text-zinc-400 bg-zinc-800/60 px-2 py-0.5 rounded border border-zinc-700">
                          {a.alert_type.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-zinc-100">{a.title}</p>
                      {a.summary && <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{a.summary}</p>}
                      <div className="flex items-center gap-3 text-xs text-zinc-400 mt-2">
                        <span>{competitorName(a.competitor_id)}</span>
                        <span>{fmtDate(a.created_at)}</span>
                      </div>
                    </div>
                    {a.status === 'open' && (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => handleAlertStatus(a.id, 'acknowledged')}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-zinc-800 text-zinc-300 border border-zinc-700 text-xs hover:bg-zinc-700 transition-all"
                        >
                          <Eye className="w-3 h-3" /> Ack
                        </button>
                        <button
                          onClick={() => handleAlertStatus(a.id, 'dismissed')}
                          className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 border border-zinc-700 hover:bg-zinc-700 transition-all"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* ── Market Intelligence Tab ───────────────────────────────────────── */}
      {tab === 'market' && (
        <motion.div
          key="market"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="space-y-5"
        >
          {/* Industry selector */}
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={selectedIndustry}
              onChange={e => setSelectedIndustry(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-violet-500"
            >
              {industries.length === 0 && <option value="">No industries tracked</option>}
              {industries.map(ind => (
                <option key={ind} value={ind}>{ind}</option>
              ))}
            </select>
            <button
              onClick={() => selectedIndustry && fetchMarketData(selectedIndustry)}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-violet-600 text-white text-xs font-medium hover:bg-violet-500 transition-all"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${phase2Loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Trend mini-chart (ASCII bar style) */}
          {trendData.length > 0 && (
            <div className="glass-panel rounded-2xl p-5">
              <h3 className="text-sm font-semibold text-zinc-300 mb-4">Volume Trend — last {trendData.length} days</h3>
              <div className="flex items-end gap-1 h-20">
                {trendData.map(pt => {
                  const max = Math.max(...trendData.map(p => p.total_volume), 1);
                  const pct = Math.max((pt.total_volume / max) * 100, 4);
                  const sentColor = pt.avg_sentiment > 0.1 ? 'bg-emerald-500' : pt.avg_sentiment < -0.1 ? 'bg-rose-500' : 'bg-zinc-400';
                  return (
                    <div key={pt.date} className="flex-1 group relative flex flex-col items-center justify-end h-full">
                      <div title={`${pt.date}: vol ${pt.total_volume}, sentiment ${pt.avg_sentiment}`}
                        className={`w-full rounded-t ${sentColor} transition-all group-hover:opacity-80`}
                        style={{ height: `${pct}%` }}
                      />
                      {pt.regulatory_events > 0 && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-amber-400" title={`${pt.regulatory_events} regulatory events`} />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-xs text-zinc-400 mt-1">
                <span>{trendData[0]?.date}</span>
                <span>{trendData[trendData.length - 1]?.date}</span>
              </div>
              <div className="flex items-center gap-3 mt-3 text-[11px] text-zinc-400">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" /> Positive</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-rose-500 inline-block" /> Negative</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-zinc-400 inline-block" /> Neutral</span>
                <span className="flex items-center gap-1 ml-auto"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> Regulatory event</span>
              </div>
            </div>
          )}

          {/* Narratives list */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-300">
              Narrative Clusters <span className="text-zinc-400 font-normal ml-1">({narratives.length})</span>
            </h3>
          </div>

          {phase2Loading ? (
            <div className="text-center py-10 text-zinc-400">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading market data…
            </div>
          ) : narratives.length === 0 ? (
            <div className="glass-panel rounded-2xl p-10 text-center">
              <TrendingUp className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
              <p className="text-zinc-400 font-medium">No narratives found</p>
              <p className="text-zinc-400 text-sm mt-1">
                Add competitors with keywords for the <strong className="text-zinc-300">{selectedIndustry}</strong> industry and run a scan
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {narratives.map(n => (
                <motion.div
                  key={n.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`glass-panel rounded-xl border p-4 ${sentimentBg(n.sentiment_score)}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium line-clamp-2 mb-2">{n.title}</p>
                      <div className="flex flex-wrap items-center gap-2 text-[11px]">
                        <span className={`font-semibold ${sentimentColor(n.sentiment_score)}`}>
                          Sentiment {n.sentiment_score > 0 ? '+' : ''}{n.sentiment_score.toFixed(2)}
                        </span>
                        <span className="text-zinc-400">•</span>
                        <span className="text-zinc-400">{n.volume} mentions</span>
                        {n.keywords.slice(0, 4).map(kw => (
                          <span key={kw} className="px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">{kw}</span>
                        ))}
                      </div>
                      {Object.keys(n.source_breakdown || {}).length > 0 && (
                        <div className="flex gap-2 mt-2 text-xs text-zinc-400">
                          {Object.entries(n.source_breakdown).slice(0, 3).map(([src, cnt]) => (
                            <span key={src}>{src}: {cnt}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Volume bar */}
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-lg font-bold text-white">{n.volume}</span>
                      <span className="text-xs text-zinc-400">mentions</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* ── Regulatory Alerts Tab ─────────────────────────────────────────── */}
      {tab === 'regulatory' && (
        <motion.div
          key="regulatory"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="space-y-5"
        >
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={selectedIndustry}
              onChange={e => setSelectedIndustry(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-violet-500"
            >
              {industries.map(ind => <option key={ind} value={ind}>{ind}</option>)}
            </select>
            <select
              value={regSevFilter}
              onChange={e => setRegSevFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none focus:border-violet-500"
            >
              <option value="all">All severities</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
            <select
              value={regStatusFilter}
              onChange={e => setRegStatusFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none focus:border-violet-500"
            >
              <option value="all">All statuses</option>
              <option value="open">Open</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="dismissed">Dismissed</option>
            </select>
            <button
              onClick={() => selectedIndustry && fetchMarketData(selectedIndustry)}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-violet-600 text-white text-xs font-medium hover:bg-violet-500 transition-all"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${phase2Loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <span className="ml-auto text-xs text-zinc-400">
              {regAlerts.filter(r =>
                (regSevFilter === 'all' || r.severity === regSevFilter) &&
                (regStatusFilter === 'all' || r.status === regStatusFilter)
              ).length} alerts
            </span>
          </div>

          {/* List */}
          {(() => {
            const filtered = regAlerts.filter(r =>
              (regSevFilter === 'all' || r.severity === regSevFilter) &&
              (regStatusFilter === 'all' || r.status === regStatusFilter)
            );
            return filtered.length === 0 ? (
              <div className="glass-panel rounded-2xl p-10 text-center">
                <Scale className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
                <p className="text-zinc-400 font-medium">No regulatory alerts</p>
                <p className="text-zinc-400 text-sm mt-1">Regulatory signals are detected during industry scans</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map(r => (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`glass-panel rounded-xl border p-4 ${SEV_COLOR[r.severity] ?? SEV_COLOR.LOW}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${SEV_COLOR[r.severity] ?? SEV_COLOR.LOW}`}>
                            {r.severity}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">
                            {r.regulation_type}
                          </span>
                          {r.jurisdiction && (
                            <span className="flex items-center gap-1 text-xs text-zinc-400">
                              <Globe className="w-3 h-3" />{r.jurisdiction}
                            </span>
                          )}
                          {r.status !== 'open' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700">
                              {r.status}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-white font-medium line-clamp-2 mb-1">{r.title}</p>
                        {r.summary && <p className="text-xs text-zinc-400 mb-2 line-clamp-2">{r.summary}</p>}
                        <div className="flex items-center gap-3 text-xs text-zinc-400">
                          {r.source_name && <span>{r.source_name}</span>}
                          {r.detected_at && <span>{fmtDate(r.detected_at)}</span>}
                          {r.source_url && (
                            <a href={r.source_url} target="_blank" rel="noopener noreferrer"
                              className="text-violet-400 hover:text-violet-300 underline">
                              Source
                            </a>
                          )}
                        </div>
                      </div>
                      {r.status === 'open' && (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button
                            onClick={() => handleRegStatus(r.id, 'acknowledged')}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-zinc-800 text-zinc-300 border border-zinc-700 text-xs hover:bg-zinc-700 transition-all"
                          >
                            <Eye className="w-3 h-3" /> Ack
                          </button>
                          <button
                            onClick={() => handleRegStatus(r.id, 'dismissed')}
                            className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 border border-zinc-700 hover:bg-zinc-700 transition-all"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            );
          })()}
        </motion.div>
      )}

      {/* ── Supply Chain Risk Tab ─────────────────────────────────────────── */}
      {tab === 'supply_risk' && (
        <motion.div
          key="supply_risk"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="space-y-5"
        >
          {/* Header row */}
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={selectedIndustry}
              onChange={e => setSelectedIndustry(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-violet-500"
            >
              {industries.length === 0 && <option value="">No industries tracked</option>}
              {industries.map(ind => <option key={ind} value={ind}>{ind}</option>)}
            </select>
            <select
              value={scRiskFilter}
              onChange={e => setScRiskFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none focus:border-violet-500"
            >
              <option value="all">All risk types</option>
              <option value="disruption">Disruption</option>
              <option value="financial">Financial</option>
              <option value="geopolitical">Geopolitical</option>
              <option value="natural_disaster">Natural Disaster</option>
              <option value="cyber">Cyber</option>
              <option value="regulatory">Regulatory</option>
            </select>
            <button
              onClick={() => selectedIndustry && fetchPhase3Data(selectedIndustry)}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-violet-600 text-white text-xs font-medium hover:bg-violet-500 transition-all"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${phase3Loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowEntityForm(v => !v)}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-zinc-800 text-zinc-300 border border-zinc-700 text-xs font-medium hover:bg-zinc-700 transition-all ml-auto"
            >
              <Plus className="w-3.5 h-3.5" /> Add Entity
            </button>
          </div>

          {/* Add entity form */}
          {showEntityForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="glass-panel rounded-2xl p-5 border border-violet-500/20 space-y-3"
            >
              <h3 className="text-sm font-semibold text-white">Track New Vendor / Supplier</h3>
              <div className="grid grid-cols-2 gap-3">
                <input
                  placeholder="Entity name *"
                  value={entityForm.name}
                  onChange={e => setEntityForm(f => ({ ...f, name: e.target.value }))}
                  className="col-span-2 bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-violet-500"
                />
                <select
                  value={entityForm.entity_type}
                  onChange={e => setEntityForm(f => ({ ...f, entity_type: e.target.value }))}
                  className="bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-violet-500"
                >
                  <option value="vendor">Vendor</option>
                  <option value="supplier">Supplier</option>
                  <option value="partner">Partner</option>
                  <option value="distributor">Distributor</option>
                </select>
                <input
                  placeholder="Country (optional)"
                  value={entityForm.country}
                  onChange={e => setEntityForm(f => ({ ...f, country: e.target.value }))}
                  className="bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-violet-500"
                />
                <input
                  placeholder="Keywords (comma-separated)"
                  value={entityForm.keywords}
                  onChange={e => setEntityForm(f => ({ ...f, keywords: e.target.value }))}
                  className="col-span-2 bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-violet-500"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleEntityCreate}
                  disabled={entitySubmitting || !entityForm.name.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-violet-600 text-white text-xs font-medium hover:bg-violet-500 disabled:opacity-50 transition-all"
                >
                  {entitySubmitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Save
                </button>
                <button
                  onClick={() => setShowEntityForm(false)}
                  className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-400 border border-zinc-700 text-xs hover:bg-zinc-700 transition-all"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          {/* Tracked entities */}
          {supplyEntities.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-zinc-100 uppercase tracking-wider mb-2">
                Tracked Entities ({supplyEntities.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {supplyEntities.map(e => (
                  <div key={e.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800 border border-zinc-700 text-xs text-zinc-300">
                    <span className="w-2 h-2 rounded-full bg-violet-400" />
                    <span className="font-medium">{e.name}</span>
                    <span className="text-zinc-400">{e.entity_type}</span>
                    {e.country && <span className="text-zinc-400">· {e.country}</span>}
                    {e.risk_score > 30 && (
                      <span className={`font-bold ${e.risk_score > 70 ? 'text-rose-400' : 'text-amber-400'}`}>
                        ⚠ {e.risk_score.toFixed(0)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Risk list */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-300">
              Supply Chain Risks <span className="text-zinc-400 font-normal ml-1">
                ({supplyRisks.filter(r => scRiskFilter === 'all' || r.risk_type === scRiskFilter).length})
              </span>
            </h3>
          </div>

          {phase3Loading ? (
            <div className="text-center py-10 text-zinc-400">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading supply chain data…
            </div>
          ) : (() => {
            const filtered = supplyRisks.filter(r => scRiskFilter === 'all' || r.risk_type === scRiskFilter);
            return filtered.length === 0 ? (
              <div className="glass-panel rounded-2xl p-10 text-center">
                <ShieldAlert className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
                <p className="text-zinc-400 font-medium">No supply chain risks detected</p>
                <p className="text-zinc-400 text-sm mt-1">Add tracked entities and run a scan</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map(r => (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`glass-panel rounded-xl border p-4 ${SEV_COLOR[r.severity] ?? SEV_COLOR.LOW}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${SEV_COLOR[r.severity] ?? SEV_COLOR.LOW}`}>
                            {r.severity}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">
                            {r.risk_type.replace('_', ' ')}
                          </span>
                          {(r.affected_countries || []).slice(0, 3).map(c => (
                            <span key={c} className="flex items-center gap-1 text-xs text-zinc-400">
                              <Globe className="w-3 h-3" />{c}
                            </span>
                          ))}
                        </div>
                        <p className="text-sm text-white font-medium line-clamp-2 mb-1">{r.title}</p>
                        {r.description && <p className="text-xs text-zinc-400 mb-1 line-clamp-1">{r.description}</p>}
                        <div className="flex items-center gap-3 text-xs text-zinc-400">
                          {r.source_name && <span>{r.source_name}</span>}
                          {r.detected_at && <span>{fmtDate(r.detected_at)}</span>}
                          {r.status !== 'open' && <span className="text-zinc-400">{r.status}</span>}
                        </div>
                      </div>
                      {r.status === 'open' && (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button
                            onClick={() => handleRiskStatus(r.id, 'acknowledged')}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-zinc-800 text-zinc-300 border border-zinc-700 text-xs hover:bg-zinc-700 transition-all"
                          >
                            <Eye className="w-3 h-3" /> Ack
                          </button>
                          <button
                            onClick={() => handleRiskStatus(r.id, 'resolved')}
                            className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 border border-zinc-700 hover:bg-zinc-700 transition-all"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            );
          })()}
        </motion.div>
      )}

      {/* ── Dark Intelligence Tab (Dark Web + M&A) ───────────────────────── */}
      {tab === 'dark_intel' && (
        <motion.div
          key="dark_intel"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="space-y-6"
        >
          {/* Industry + refresh */}
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={selectedIndustry}
              onChange={e => setSelectedIndustry(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-violet-500"
            >
              {industries.map(ind => <option key={ind} value={ind}>{ind}</option>)}
            </select>
            <button
              onClick={() => selectedIndustry && fetchPhase3Data(selectedIndustry)}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-violet-600 text-white text-xs font-medium hover:bg-violet-500 transition-all"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${phase3Loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* ── Dark Web Alerts ── */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <h3 className="text-sm font-semibold text-zinc-300">Dark Web Alerts</h3>
              <select
                value={dwTypeFilter}
                onChange={e => setDwTypeFilter(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-violet-500"
              >
                <option value="all">All types</option>
                <option value="credential_leak">Credential Leak</option>
                <option value="data_breach">Data Breach</option>
                <option value="counterfeit">Counterfeit</option>
                <option value="ip_theft">IP Theft</option>
                <option value="strategic_leak">Strategic Leak</option>
                <option value="vendor_discussion">Vendor Discussion</option>
              </select>
              <span className="text-xs text-zinc-400 ml-auto">
                {darkWebAlerts.filter(a => dwTypeFilter === 'all' || a.alert_type === dwTypeFilter).length} alerts
              </span>
            </div>

            {(() => {
              const filtered = darkWebAlerts.filter(a => dwTypeFilter === 'all' || a.alert_type === dwTypeFilter);
              return filtered.length === 0 ? (
                <div className="glass-panel rounded-2xl p-8 text-center">
                  <ShieldAlert className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
                  <p className="text-zinc-400 text-sm">No dark web alerts detected</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filtered.slice(0, 15).map(a => (
                    <motion.div
                      key={a.id}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`glass-panel rounded-xl border p-4 ${SEV_COLOR[a.severity] ?? SEV_COLOR.MEDIUM}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${SEV_COLOR[a.severity] ?? SEV_COLOR.MEDIUM}`}>
                              {a.severity}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-900 text-zinc-300 border border-zinc-700">
                              {a.alert_type.replace(/_/g, ' ')}
                            </span>
                            {a.affected_entity && (
                              <span className="text-xs text-violet-300 font-medium">{a.affected_entity}</span>
                            )}
                          </div>
                          <p className="text-sm text-white font-medium line-clamp-2 mb-1">{a.title}</p>
                          {a.summary && <p className="text-xs text-zinc-400 mb-1 line-clamp-1">{a.summary}</p>}
                          <div className="text-xs text-zinc-400 flex gap-3">
                            {a.detected_at && <span>{fmtDate(a.detected_at)}</span>}
                            {a.status !== 'open' && <span>{a.status}</span>}
                          </div>
                        </div>
                        {a.status === 'open' && (
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button
                              onClick={() => handleDwStatus(a.id, 'acknowledged')}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-zinc-800 text-zinc-300 border border-zinc-700 text-xs hover:bg-zinc-700 transition-all"
                            >
                              <Eye className="w-3 h-3" /> Ack
                            </button>
                            <button
                              onClick={() => handleDwStatus(a.id, 'dismissed')}
                              className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 border border-zinc-700 hover:bg-zinc-700 transition-all"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* ── M&A Signals ── */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <h3 className="text-sm font-semibold text-zinc-300">M&amp;A &amp; Partnership Signals</h3>
              <select
                value={maTypeFilter}
                onChange={e => setMATypeFilter(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-violet-500"
              >
                <option value="all">All types</option>
                <option value="acquisition">Acquisition</option>
                <option value="merger">Merger</option>
                <option value="partnership">Partnership</option>
                <option value="investment">Investment</option>
                <option value="ipo">IPO</option>
                <option value="spinoff">Spinoff</option>
                <option value="joint_venture">Joint Venture</option>
              </select>
              <span className="text-xs text-zinc-400 ml-auto">
                {maSignals.filter(s => maTypeFilter === 'all' || s.signal_type === maTypeFilter).length} signals
              </span>
            </div>

            {(() => {
              const filtered = maSignals.filter(s => maTypeFilter === 'all' || s.signal_type === maTypeFilter);
              return filtered.length === 0 ? (
                <div className="glass-panel rounded-2xl p-8 text-center">
                  <Handshake className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
                  <p className="text-zinc-400 text-sm">No M&amp;A signals detected</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filtered.slice(0, 15).map(s => {
                    const confPct = Math.round(s.confidence * 100);
                    const confColor = confPct >= 75 ? 'text-emerald-400' : confPct >= 50 ? 'text-amber-400' : 'text-zinc-400';
                    return (
                      <motion.div
                        key={s.id}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="glass-panel rounded-xl border border-violet-500/15 bg-violet-500/5 p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300 border border-violet-500/30">
                                {s.signal_type.replace('_', ' ')}
                              </span>
                              <span className={`text-xs font-semibold ${confColor}`}>
                                {confPct}% confidence
                              </span>
                              {s.deal_value && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                                  {s.deal_value}
                                </span>
                              )}
                              {s.status !== 'signal' && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700">
                                  {s.status}
                                </span>
                              )}
                            </div>
                            {(s.acquirer || s.target) && (
                              <p className="text-xs text-zinc-300 mb-1">
                                {s.acquirer && <span className="font-semibold text-white">{s.acquirer}</span>}
                                {s.acquirer && s.target && <span className="text-zinc-400 mx-1.5">→</span>}
                                {s.target && <span className="font-semibold text-violet-300">{s.target}</span>}
                              </p>
                            )}
                            <p className="text-sm text-white line-clamp-2 mb-1">{s.title}</p>
                            <div className="text-xs text-zinc-400 flex gap-3">
                              {s.source_name && <span>{s.source_name}</span>}
                              {s.detected_at && <span>{fmtDate(s.detected_at)}</span>}
                              {s.source_url && (
                                <a href={s.source_url} target="_blank" rel="noopener noreferrer"
                                  className="text-violet-400 hover:text-violet-300 underline">
                                  Source
                                </a>
                              )}
                            </div>
                          </div>
                          {s.status === 'signal' && (
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <button
                                onClick={() => handleMAStatus(s.id, 'confirmed')}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-zinc-800 text-zinc-300 border border-zinc-700 text-xs hover:bg-zinc-700 transition-all"
                              >
                                <Check className="w-3 h-3" /> Confirm
                              </button>
                              <button
                                onClick={() => handleMAStatus(s.id, 'rumor')}
                                className="px-2.5 py-1 rounded-lg bg-zinc-800 text-zinc-400 border border-zinc-700 text-xs hover:bg-zinc-700 transition-all"
                              >
                                Rumor
                              </button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </motion.div>
      )}

      {/* ── Phase 4: Risk Forecast ────────────────────────────────────────── */}
      {tab === 'risk_forecast' && (
        <motion.div key="risk_forecast" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {/* Controls */}
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={forecastType}
              onChange={e => setForecastType(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-300"
            >
              {['supply_chain','dark_web','competitive','regulatory','geopolitical','financial','reputational'].map(t => (
                <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
              ))}
            </select>
            <button
              onClick={handleGenerateForecast}
              disabled={phase4Loading || !selectedIndustry}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium disabled:opacity-50 transition-all"
            >
              <Zap className={`w-3.5 h-3.5 ${phase4Loading ? 'animate-pulse' : ''}`} />
              Generate Forecast
            </button>
            <button
              onClick={() => selectedIndustry && fetchPhase4Data(selectedIndustry)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-400 border border-zinc-700 text-xs hover:bg-zinc-700 transition-all"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${phase4Loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => handleGenerateReport('weekly_brief')}
              disabled={phase4Loading || !selectedIndustry}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-300 border border-zinc-700 text-xs hover:bg-zinc-700 transition-all disabled:opacity-50"
            >
              <FileText className="w-3.5 h-3.5" /> Generate Weekly Brief
            </button>
          </div>

          {/* Forecasts */}
          {phase4Loading ? (
            <div className="flex items-center justify-center h-24 text-zinc-400 text-sm">
              <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Generating forecast…
            </div>
          ) : forecasts.length === 0 ? (
            <div className="text-zinc-400 text-sm text-center py-10">No forecasts yet. Click Generate Forecast to create one.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {forecasts.map(f => {
                const trendColor = f.trend === 'rising' ? 'text-red-400' : f.trend === 'falling' ? 'text-emerald-400' : 'text-zinc-400';
                const parseList = (s: string | null) => { try { return JSON.parse(s || '[]') as string[]; } catch { return []; } };
                return (
                  <motion.div key={f.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-xs font-semibold text-violet-400 uppercase tracking-wide">{f.forecast_type.replace(/_/g, ' ')}</div>
                        <div className="text-xs text-zinc-400 mt-0.5">{f.entity_name || f.industry} · {f.horizon_days}d horizon</div>
                      </div>
                      <div className={`flex items-center gap-1 text-xs font-semibold ${trendColor}`}>
                        {f.trend === 'rising' ? '↑' : f.trend === 'falling' ? '↓' : '→'} {f.trend}
                      </div>
                    </div>
                    {/* Score bars */}
                    <div className="space-y-1.5">
                      {[
                        { label: 'Now', score: f.risk_score_now },
                        { label: '7d', score: f.risk_score_7d ?? 0 },
                        { label: '30d', score: f.risk_score_30d ?? 0 },
                        { label: '90d', score: f.risk_score_90d ?? 0 },
                      ].map(({ label, score }) => {
                        const barColor = score >= 70 ? 'bg-red-500' : score >= 45 ? 'bg-amber-500' : 'bg-emerald-500';
                        return (
                          <div key={label} className="flex items-center gap-2">
                            <div className="w-8 text-right text-xs text-zinc-400">{label}</div>
                            <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                              <div className={`h-full ${barColor} rounded-full`} style={{ width: `${score}%` }} />
                            </div>
                            <div className="w-8 text-xs text-zinc-300 font-mono">{score.toFixed(0)}</div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Drivers */}
                    {parseList(f.drivers).length > 0 && (
                      <div className="space-y-1">
                        <div className="text-xs text-zinc-400 font-medium">Key Drivers</div>
                        {parseList(f.drivers).slice(0, 3).map((d, i) => (
                          <div key={i} className="text-xs text-zinc-400 flex gap-1.5"><ChevronRight className="w-3 h-3 text-violet-500 mt-0.5 shrink-0" />{d}</div>
                        ))}
                      </div>
                    )}
                    {/* Confidence */}
                    <div className="flex items-center justify-between text-xs text-zinc-400">
                      <span>Confidence: <span className="text-zinc-300">{(f.confidence * 100).toFixed(0)}%</span></span>
                      <span>{f.data_points_used} data points</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Strategic Reports */}
          {reports.length > 0 && (
            <div className="mt-6 space-y-3">
              <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" /> Strategic Reports
              </div>
              {reports.map(r => {
                const isExpanded = expandedReport === r.id;
                const parseList = (s: string | null) => { try { return JSON.parse(s || '[]') as string[]; } catch { return []; } };
                return (
                  <div key={r.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setExpandedReport(isExpanded ? null : r.id)}
                      className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-zinc-800/50 transition-all"
                    >
                      <div>
                        <div className="text-xs font-medium text-zinc-200">{r.title}</div>
                        <div className="text-xs text-zinc-400 mt-0.5">{r.report_type.replace(/_/g, ' ')} · {r.generated_at?.slice(0, 10)}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.status === 'published' ? 'bg-emerald-900/40 text-emerald-400' : 'bg-zinc-800 text-zinc-400'}`}>{r.status}</span>
                        <ChevronRight className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-3 border-t border-zinc-800">
                        {r.executive_summary && (
                          <p className="text-xs text-zinc-300 mt-3">{r.executive_summary}</p>
                        )}
                        {parseList(r.key_findings).length > 0 && (
                          <div>
                            <div className="text-xs text-zinc-400 font-medium mb-1">Key Findings</div>
                            {parseList(r.key_findings).map((f, i) => <div key={i} className="text-xs text-zinc-400 flex gap-1.5"><ChevronRight className="w-3 h-3 text-violet-500 mt-0.5 shrink-0" />{f}</div>)}
                          </div>
                        )}
                        {parseList(r.risk_highlights).length > 0 && (
                          <div>
                            <div className="text-xs text-zinc-400 font-medium mb-1">Risk Highlights</div>
                            {parseList(r.risk_highlights).map((f, i) => <div key={i} className="text-xs text-red-400 flex gap-1.5"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{f}</div>)}
                          </div>
                        )}
                        {parseList(r.opportunities).length > 0 && (
                          <div>
                            <div className="text-xs text-zinc-400 font-medium mb-1">Opportunities</div>
                            {parseList(r.opportunities).map((f, i) => <div key={i} className="text-xs text-emerald-400 flex gap-1.5"><Check className="w-3 h-3 mt-0.5 shrink-0" />{f}</div>)}
                          </div>
                        )}
                        {parseList(r.recommended_actions).length > 0 && (
                          <div>
                            <div className="text-xs text-zinc-400 font-medium mb-1">Recommended Actions</div>
                            {parseList(r.recommended_actions).map((f, i) => <div key={i} className="text-xs text-zinc-300 flex gap-1.5"><Zap className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />{f}</div>)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      )}

      {/* ── Phase 4: Scenario Simulator ──────────────────────────────────────── */}
      {tab === 'scenario_sim' && (
        <motion.div key="scenario_sim" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {/* Scenario Builder */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
            <div className="text-xs font-semibold text-zinc-300 flex items-center gap-2">
              <Swords className="w-3.5 h-3.5 text-violet-400" /> New Scenario
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                placeholder="Scenario name *"
                value={scenarioForm.name}
                onChange={e => setScenarioForm(s => ({ ...s, name: e.target.value }))}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500 col-span-full"
              />
              <select
                value={scenarioForm.scenario_type}
                onChange={e => setScenarioForm(s => ({ ...s, scenario_type: e.target.value }))}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-300"
              >
                {['competitive','market_entry','supply_shock','regulatory','cyber_attack','geopolitical','ma_impact'].map(t => (
                  <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                ))}
              </select>
              <input
                placeholder="Description"
                value={scenarioForm.description}
                onChange={e => setScenarioForm(s => ({ ...s, description: e.target.value }))}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-0.5">
                <div className="text-xs text-zinc-400">Threat level <span className="text-zinc-300">{scenarioForm.threat_level}</span></div>
                <input type="range" min="0" max="1" step="0.1" value={scenarioForm.threat_level}
                  onChange={e => setScenarioForm(s => ({ ...s, threat_level: e.target.value }))}
                  className="w-full accent-violet-500" />
              </div>
              <div className="space-y-0.5">
                <div className="text-xs text-zinc-400">Market share at risk <span className="text-zinc-300">{scenarioForm.market_share}</span></div>
                <input type="range" min="0" max="1" step="0.1" value={scenarioForm.market_share}
                  onChange={e => setScenarioForm(s => ({ ...s, market_share: e.target.value }))}
                  className="w-full accent-violet-500" />
              </div>
              <div className="space-y-0.5">
                <div className="text-xs text-zinc-400">Duration (months) <span className="text-zinc-300">{scenarioForm.duration_months}</span></div>
                <input type="range" min="1" max="24" step="1" value={scenarioForm.duration_months}
                  onChange={e => setScenarioForm(s => ({ ...s, duration_months: e.target.value }))}
                  className="w-full accent-violet-500" />
              </div>
            </div>
            <button
              onClick={handleRunScenario}
              disabled={scenarioRunning || !scenarioForm.name.trim() || !selectedIndustry}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium disabled:opacity-50 transition-all"
            >
              {scenarioRunning ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Brain className="w-3.5 h-3.5" />}
              Run Scenario
            </button>
          </div>

          {/* Scenario list */}
          {scenarios.length === 0 ? (
            <div className="text-zinc-400 text-sm text-center py-8">No scenarios yet. Build one above.</div>
          ) : (
            <div className="space-y-3">
              {scenarios.map(s => {
                const parseList = (v: string | null) => { try { return JSON.parse(v || '[]') as string[]; } catch { return []; } };
                const scoreColor = s.outcome_score >= 70 ? 'text-red-400' : s.outcome_score >= 40 ? 'text-amber-400' : 'text-emerald-400';
                const probPct = Math.round(s.probability * 100);
                return (
                  <motion.div key={s.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-xs font-semibold text-zinc-200">{s.name}</div>
                        <div className="text-xs text-zinc-400 mt-0.5">{s.scenario_type.replace(/_/g, ' ')} · {s.created_at?.slice(0, 10)}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`text-lg font-bold tabular-nums ${scoreColor}`}>{s.outcome_score.toFixed(0)}</div>
                        <div className="text-xs text-zinc-400">impact score</div>
                      </div>
                    </div>
                    {/* Impact areas */}
                    {parseList(s.impact_areas).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {parseList(s.impact_areas).map(a => (
                          <span key={a} className="px-2 py-0.5 rounded-full bg-violet-900/30 text-violet-300 text-xs border border-violet-800/40">{a}</span>
                        ))}
                      </div>
                    )}
                    {/* Probability bar */}
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-zinc-400 w-20">Probability</div>
                      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full bg-violet-500 rounded-full" style={{ width: `${probPct}%` }} />
                      </div>
                      <div className="text-xs text-zinc-300 font-mono w-10">{probPct}%</div>
                    </div>
                    {/* Recommendations */}
                    {s.recommendations && (
                      <div className="text-xs text-zinc-300 border-t border-zinc-800 pt-2">
                        <span className="text-zinc-400 font-medium mr-1">Recommendations:</span>
                        {s.recommendations.replace(/^\[.*?\]\s*/, '')}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* War-game sessions */}
          <div className="mt-6 space-y-3">
            <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide flex items-center gap-2">
              <Swords className="w-3.5 h-3.5" /> War-game / Red-team Sessions
            </div>
            {/* Create war-game */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input placeholder="Session title *" value={wgForm.title}
                  onChange={e => setWGForm(w => ({ ...w, title: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500 col-span-full" />
                <input placeholder="Our company" value={wgForm.our_company}
                  onChange={e => setWGForm(w => ({ ...w, our_company: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500" />
                <input placeholder="Adversary" value={wgForm.adversary}
                  onChange={e => setWGForm(w => ({ ...w, adversary: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500" />
                <select value={wgForm.game_type} onChange={e => setWGForm(w => ({ ...w, game_type: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-300">
                  {['competitive_response','market_entry_defense','crisis_simulation','supply_disruption','cyber_incident'].map(t => (
                    <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                  ))}
                </select>
                <button onClick={handleCreateWargame}
                  disabled={wgCreating || !wgForm.title.trim() || !wgForm.our_company.trim() || !wgForm.adversary.trim() || !selectedIndustry}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-zinc-200 text-xs font-medium disabled:opacity-50 transition-all">
                  {wgCreating ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  Create Session
                </button>
              </div>
            </div>
            {/* War-game list */}
            {wargames.map(wg => {
              const isExpanded = expandedWG === wg.id;
              const moves = (() => { try { return JSON.parse(wg.moves || '[]') as any[]; } catch { return []; } })();
              const lessons = (() => { try { return JSON.parse(wg.lessons_learned || '[]') as string[]; } catch { return []; } })();
              const usColor = wg.final_score_us >= wg.final_score_adv ? 'text-emerald-400' : 'text-red-400';
              const advColor = wg.final_score_adv > wg.final_score_us ? 'text-red-400' : 'text-zinc-400';
              return (
                <div key={wg.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                  <button onClick={() => setExpandedWG(isExpanded ? null : wg.id)}
                    className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-zinc-800/50 transition-all">
                    <div>
                      <div className="text-xs font-medium text-zinc-200">{wg.title}</div>
                      <div className="text-xs text-zinc-400">{wg.our_company} vs {wg.adversary} · {wg.game_type.replace(/_/g, ' ')}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-xs font-mono">
                        <span className={usColor}>{wg.final_score_us}</span>
                        <span className="text-zinc-400"> : </span>
                        <span className={advColor}>{wg.final_score_adv}</span>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${wg.status === 'complete' ? 'bg-emerald-900/40 text-emerald-400' : wg.status === 'active' ? 'bg-violet-900/40 text-violet-400' : 'bg-zinc-800 text-zinc-400'}`}>{wg.status}</span>
                      <ChevronRight className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3 border-t border-zinc-800">
                      {wg.outcome_summary && <p className="text-xs text-zinc-300 mt-3">{wg.outcome_summary}</p>}
                      {moves.length > 0 && (
                        <div className="space-y-1.5">
                          <div className="text-xs text-zinc-400 font-medium">Moves ({moves.length})</div>
                          {moves.map((m: any) => (
                            <div key={m.seq} className="flex gap-2 text-xs">
                              <span className={`shrink-0 font-mono w-6 text-center rounded ${m.actor === 'us' ? 'text-violet-400' : 'text-amber-400'}`}>{m.seq}.</span>
                              <div>
                                <span className="font-medium text-zinc-300">{m.actor === 'us' ? wg.our_company : wg.adversary}</span>
                                <span className="text-zinc-400 mx-1">→</span>
                                <span className="text-zinc-400">{m.move.replace(/_/g, ' ')}</span>
                                {m.recommended_response && (
                                  <div className="text-zinc-400 mt-0.5 pl-1 border-l border-zinc-700">Response: {m.recommended_response}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {lessons.length > 0 && (
                        <div className="space-y-1">
                          <div className="text-xs text-zinc-400 font-medium">Lessons Learned</div>
                          {lessons.map((l, i) => <div key={i} className="text-xs text-zinc-400 flex gap-1.5"><ChevronRight className="w-3 h-3 text-violet-500 mt-0.5 shrink-0" />{l}</div>)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* ── Phase 5: Intelligence Feed ────────────────────────────────────── */}
      {tab === 'intel_feed' && (
        <motion.div key="intel_feed" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">

          {/* Correlation banner */}
          {correlation && (() => {
            const lvl = correlation.overall_risk_level;
            const bg = lvl === 'CRITICAL' ? 'bg-red-950/50 border-red-800' : lvl === 'HIGH' ? 'bg-amber-950/50 border-amber-800' : lvl === 'MODERATE' ? 'bg-yellow-950/50 border-yellow-800' : 'bg-zinc-900 border-zinc-800';
            const txt = lvl === 'CRITICAL' ? 'text-red-400' : lvl === 'HIGH' ? 'text-amber-400' : lvl === 'MODERATE' ? 'text-yellow-400' : 'text-emerald-400';
            return (
              <div className={`rounded-xl border px-4 py-3 ${bg}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <Radio className={`w-4 h-4 ${txt}`} />
                    <div>
                      <div className="text-xs font-semibold text-zinc-200">
                        Overall Risk: <span className={txt}>{lvl}</span>
                        <span className="text-zinc-400 font-normal ml-2">({correlation.combined_risk_score}/100)</span>
                      </div>
                      <div className="text-xs text-zinc-400 mt-0.5">
                        {Object.entries(correlation.signal_counts).map(([k, v]) => `${k.replace(/_/g,' ')}: ${v}`).join(' · ')}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => selectedIndustry && fetchPhase5Data(selectedIndustry)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-400 border border-zinc-700 text-xs hover:bg-zinc-700 transition-all"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${phase5Loading ? 'animate-spin' : ''}`} /> Refresh
                  </button>
                </div>
                {correlation.recommendations.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {correlation.recommendations.map((r, i) => (
                      <span key={i} className="text-xs text-zinc-300 bg-zinc-800/60 px-2 py-0.5 rounded-md">{r}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <select value={feedPhaseFilter} onChange={e => setFeedPhaseFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-300">
              <option value="all">All phases</option>
              {['phase1','phase2','phase3','phase4'].map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <select value={feedSevFilter} onChange={e => setFeedSevFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-300">
              <option value="all">All severities</option>
              {['CRITICAL','HIGH','MEDIUM','LOW'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <a href={selectedIndustry ? `${API_BASE_URL}/api/bi/fusion/export/${encodeURIComponent(selectedIndustry)}?days=30` : '#'}
              target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-300 border border-zinc-700 text-xs hover:bg-zinc-700 transition-all">
              <BookOpen className="w-3.5 h-3.5" /> Export JSON
            </a>
          </div>

          {/* Feed */}
          {phase5Loading ? (
            <div className="flex items-center justify-center h-24 text-zinc-400 text-sm">
              <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Building feed…
            </div>
          ) : (
            <div className="space-y-2">
              {feedEvents
                .filter(e => feedPhaseFilter === 'all' || e.source_phase === feedPhaseFilter)
                .filter(e => feedSevFilter === 'all' || e.severity === feedSevFilter)
                .map(ev => {
                  const sevColor: Record<string,string> = { CRITICAL:'text-red-400 bg-red-950/40 border-red-800/50', HIGH:'text-amber-400 bg-amber-950/40 border-amber-800/50', MEDIUM:'text-yellow-400 bg-yellow-950/40 border-yellow-800/50', LOW:'text-zinc-400 bg-zinc-900 border-zinc-800' };
                  const phaseTag: Record<string,string> = { phase1:'Competitor', phase2:'Market', phase3:'Supply/Dark', phase4:'Predictive', watchlist:'Watchlist' };
                  const tags = (() => { try { return JSON.parse(ev.tags || '[]') as string[]; } catch { return []; } })();
                  return (
                    <motion.div key={ev.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      className={`rounded-xl border px-4 py-3 ${ev.is_read ? 'opacity-60' : ''} ${sevColor[ev.severity] || sevColor.LOW}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-medium text-zinc-200 truncate">{ev.title}</span>
                            <span className="shrink-0 px-1.5 py-0.5 rounded text-xs bg-zinc-800 text-zinc-400">{phaseTag[ev.source_phase] || ev.source_phase}</span>
                            <span className="shrink-0 px-1.5 py-0.5 rounded text-xs bg-zinc-800/60 text-zinc-400">{ev.source_type.replace(/_/g,' ')}</span>
                          </div>
                          {ev.summary && <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{ev.summary}</p>}
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {tags.slice(0,4).map(t => (
                              <span key={t} className="text-xs text-zinc-400 bg-zinc-800/40 px-1.5 py-0.5 rounded">{t}</span>
                            ))}
                            <span className="text-xs text-zinc-400">{ev.occurred_at?.slice(0,10)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {!ev.is_read && (
                            <button onClick={() => handleMarkRead(ev.id)}
                              title="Mark read"
                              className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 hover:text-zinc-300 transition-all">
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              }
              {feedEvents.filter(e => feedPhaseFilter === 'all' || e.source_phase === feedPhaseFilter).filter(e => feedSevFilter === 'all' || e.severity === feedSevFilter).length === 0 && (
                <div className="text-zinc-400 text-sm text-center py-10">
                  No feed events. Click Refresh or ensure signals exist for this industry.
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* ── Phase 5: Watchlist & Rules ───────────────────────────────────────── */}
      {tab === 'watchlist' && (
        <motion.div key="watchlist" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">

          {/* Watchlist */}
          <div className="space-y-3">
            <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide flex items-center gap-2">
              <Eye className="w-3.5 h-3.5" /> Watchlist Entries
            </div>
            {/* Add entry */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input placeholder="Entity / topic label *" value={watchForm.label}
                  onChange={e => setWatchForm(w => ({ ...w, label: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500" />
                <input placeholder="Keywords (comma-separated)" value={watchForm.keywords}
                  onChange={e => setWatchForm(w => ({ ...w, keywords: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500" />
                <select value={watchForm.entity_type} onChange={e => setWatchForm(w => ({ ...w, entity_type: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-300">
                  {['company','executive','product','regulation','technology','threat_actor','topic'].map(t => (
                    <option key={t} value={t}>{t.replace(/_/g,' ')}</option>
                  ))}
                </select>
              </div>
              <button onClick={handleAddWatchlist}
                disabled={watchSubmitting || !watchForm.label.trim() || !selectedIndustry}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-zinc-200 text-xs font-medium disabled:opacity-50 transition-all">
                {watchSubmitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Add to Watchlist
              </button>
            </div>
            {/* List */}
            {watchlistItems.length === 0 ? (
              <div className="text-zinc-400 text-sm text-center py-6">No watchlist entries yet.</div>
            ) : (
              <div className="space-y-2">
                {watchlistItems.map(e => {
                  const kws: string[] = (() => { try { return JSON.parse(e.keywords || '[]'); } catch { return []; } })();
                  return (
                    <div key={e.id} className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-zinc-200">{e.label}</span>
                          <span className="px-1.5 py-0.5 rounded text-xs bg-zinc-800 text-zinc-400">{e.entity_type}</span>
                          {e.hit_count > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-violet-900/40 text-violet-300 border border-violet-800/40">{e.hit_count} hits</span>
                          )}
                        </div>
                        {kws.length > 0 && (
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {kws.map(k => <span key={k} className="text-xs text-zinc-400 bg-zinc-800/60 px-1.5 py-0.5 rounded">{k}</span>)}
                          </div>
                        )}
                      </div>
                      <button onClick={() => handleRemoveWatchlist(e.id)}
                        className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 hover:text-red-400 transition-all shrink-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Notification Rules */}
          <div className="space-y-3">
            <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide flex items-center gap-2">
              <BellRing className="w-3.5 h-3.5" /> Notification Rules
            </div>
            {/* Add rule */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input placeholder="Rule name *" value={ruleForm.name}
                  onChange={e => setRuleForm(r => ({ ...r, name: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500 col-span-full" />
                <select value={ruleForm.trigger_phase} onChange={e => setRuleForm(r => ({ ...r, trigger_phase: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-300">
                  {['any','phase1','phase2','phase3','phase4'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <select value={ruleForm.trigger_type} onChange={e => setRuleForm(r => ({ ...r, trigger_type: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-300">
                  {['any','supply_risk','dark_web_alert','ma_signal','risk_forecast','scenario','bi_alert'].map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
                </select>
                <select value={ruleForm.min_severity} onChange={e => setRuleForm(r => ({ ...r, min_severity: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-300">
                  {['LOW','MEDIUM','HIGH','CRITICAL'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={ruleForm.action} onChange={e => setRuleForm(r => ({ ...r, action: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-300">
                  {['feed','email','slack','webhook'].map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                <input placeholder="Keywords (comma-separated)" value={ruleForm.keywords}
                  onChange={e => setRuleForm(r => ({ ...r, keywords: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500 col-span-full" />
              </div>
              <button onClick={handleAddRule}
                disabled={ruleSubmitting || !ruleForm.name.trim() || !selectedIndustry}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium disabled:opacity-50 transition-all">
                {ruleSubmitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
                Create Rule
              </button>
            </div>
            {/* List */}
            {notifRules.length === 0 ? (
              <div className="text-zinc-400 text-sm text-center py-6">No notification rules yet.</div>
            ) : (
              <div className="space-y-2">
                {notifRules.map(r => (
                  <div key={r.id} className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-zinc-200">{r.name}</span>
                        <span className="px-1.5 py-0.5 rounded text-xs bg-zinc-800 text-zinc-400">{r.trigger_phase}/{r.trigger_type}</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${r.min_severity === 'CRITICAL' ? 'bg-red-900/40 text-red-400' : r.min_severity === 'HIGH' ? 'bg-amber-900/40 text-amber-400' : 'bg-zinc-800 text-zinc-400'}`}>{r.min_severity}+</span>
                        <span className="px-1.5 py-0.5 rounded text-xs bg-zinc-800 text-zinc-400">{r.action}</span>
                        {r.fire_count > 0 && <span className="text-xs text-violet-400">{r.fire_count}× fired</span>}
                      </div>
                      {r.keywords && (() => { const kws = (() => { try { return JSON.parse(r.keywords); } catch { return []; } })(); return kws.length > 0 ? (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {kws.map((k: string) => <span key={k} className="text-xs text-zinc-400 bg-zinc-800/60 px-1.5 py-0.5 rounded">{k}</span>)}
                        </div>
                      ) : null; })()}
                    </div>
                    <button onClick={() => handleRemoveRule(r.id)}
                      className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 hover:text-red-400 transition-all shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* ── Phase 6: Executive Briefs ────────────────────────────────────── */}
      {tab === 'exec_briefs' && (
        <motion.div key="exec_briefs" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {/* Header */}
          <div className="glass-panel rounded-2xl p-4 flex flex-wrap items-center gap-3">
            <Newspaper className="w-5 h-5 text-violet-400" />
            <span className="text-sm font-medium text-zinc-200 flex-1">C-Suite Intelligence Briefs</span>
            {(['daily','weekly','monthly','crisis'] as const).map(type => (
              <button key={type} onClick={() => handleGenerateBrief(type)}
                disabled={briefGenerating || !selectedIndustry}
                className="px-3 py-1.5 rounded-lg bg-violet-600/80 hover:bg-violet-500 text-white text-xs font-medium capitalize disabled:opacity-40 transition-all">
                {briefGenerating ? <RefreshCw className="w-3 h-3 animate-spin inline mr-1" /> : null}
                Generate {type}
              </button>
            ))}
            <button onClick={() => selectedIndustry && fetchPhase6Data(selectedIndustry)}
              disabled={phase6Loading || !selectedIndustry}
              className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-400 transition-all">
              <RefreshCw className={`w-3.5 h-3.5 ${phase6Loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Benchmark Panel */}
          <div className="glass-panel rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart2 className="w-4 h-4 text-cyan-400" />
                <span className="text-sm font-medium text-zinc-200">Industry Risk Benchmark</span>
              </div>
              <button onClick={handleGenerateBenchmark} disabled={benchGenerating || !selectedIndustry}
                className="px-3 py-1.5 rounded-lg bg-cyan-600/70 hover:bg-cyan-500 text-white text-xs font-medium disabled:opacity-40 transition-all">
                {benchGenerating ? <RefreshCw className="w-3 h-3 animate-spin inline mr-1" /> : null}
                Run Benchmark
              </button>
            </div>
            {benchmarks.length === 0 ? (
              <div className="text-zinc-400 text-sm text-center py-4">No benchmark data. Click "Run Benchmark" to generate.</div>
            ) : (() => {
              const b = benchmarks[0];
              const peers: Record<string,number> = (() => { try { return JSON.parse(b.peer_scores || '{}'); } catch { return {}; } })();
              const trend_color = b.trend === 'rising' ? 'text-rose-400' : b.trend === 'falling' ? 'text-emerald-400' : 'text-zinc-400';
              return (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-zinc-900 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-violet-400">{b.current_score.toFixed(0)}</div>
                    <div className="text-xs text-zinc-400 mt-1">Current Score</div>
                  </div>
                  <div className="bg-zinc-900 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-zinc-300">{b.baseline_score?.toFixed(0) ?? '—'}</div>
                    <div className="text-xs text-zinc-400 mt-1">Baseline (30d avg)</div>
                  </div>
                  <div className="bg-zinc-900 rounded-xl p-3 text-center">
                    <div className={`text-2xl font-bold ${b.delta_vs_baseline >= 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                      {b.delta_vs_baseline >= 0 ? '+' : ''}{b.delta_vs_baseline.toFixed(1)}
                    </div>
                    <div className="text-xs text-zinc-400 mt-1">Delta vs Baseline</div>
                  </div>
                  <div className="bg-zinc-900 rounded-xl p-3 text-center">
                    <div className={`text-2xl font-bold ${trend_color} capitalize`}>{b.trend}</div>
                    <div className="text-xs text-zinc-400 mt-1">Trend</div>
                  </div>
                  {Object.keys(peers).length > 0 && (
                    <div className="col-span-full bg-zinc-900/60 rounded-xl p-3">
                      <div className="text-xs text-zinc-400 mb-2 uppercase tracking-wider">Peer Comparison</div>
                      <div className="flex flex-wrap gap-3">
                        <div className="flex flex-col items-center">
                          <div className="text-sm font-bold text-violet-400">{b.current_score.toFixed(0)}</div>
                          <div className="text-xs text-zinc-400">{b.industry ?? 'You'}</div>
                        </div>
                        {Object.entries(peers).map(([name, score]) => (
                          <div key={name} className="flex flex-col items-center">
                            <div className="text-sm font-bold text-zinc-300">{(score as number).toFixed(0)}</div>
                            <div className="text-xs text-zinc-400">{name}</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-zinc-400">Percentile rank: <span className="text-cyan-400 font-medium">{b.percentile?.toFixed(0) ?? '—'}%</span></div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Briefings list */}
          {execBriefs.length === 0 ? (
            <div className="glass-panel rounded-2xl p-8 text-center text-zinc-400 text-sm">
              No briefings yet. Select an industry and click "Generate daily / weekly / monthly".
            </div>
          ) : (
            <div className="space-y-3">
              {execBriefs.map(b => {
                const risk_color = b.overall_risk === 'CRITICAL' ? 'text-rose-400 border-rose-500/30 bg-rose-500/5'
                  : b.overall_risk === 'HIGH' ? 'text-amber-400 border-amber-500/30 bg-amber-500/5'
                  : b.overall_risk === 'MODERATE' ? 'text-yellow-400 border-yellow-500/30 bg-yellow-500/5'
                  : 'text-cyan-400 border-cyan-500/30 bg-cyan-500/5';
                const sections: {title:string; content:string; priority:number}[] = (() => { try { return JSON.parse(b.sections || '[]'); } catch { return []; } })();
                const metrics: Record<string,number|string> = (() => { try { return JSON.parse(b.key_metrics || '{}'); } catch { return {}; } })();
                const actions: string[] = (() => { try { return JSON.parse(b.action_items || '[]'); } catch { return []; } })();
                const isExpanded = expandedBrief === b.id;
                return (
                  <div key={b.id} className={`glass-panel rounded-2xl border ${risk_color} overflow-hidden`}>
                    <div className="px-5 py-4 flex items-start gap-3 cursor-pointer" onClick={() => setExpandedBrief(isExpanded ? null : b.id)}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${risk_color}`}>{b.overall_risk}</span>
                          <span className="px-2 py-0.5 rounded text-xs bg-zinc-800 text-zinc-400 capitalize">{b.briefing_type}</span>
                          <span className="text-xs text-zinc-400">{fmtDate(b.generated_at)}</span>
                          <span className="text-xs text-zinc-400">Score: {b.risk_score.toFixed(0)}/100</span>
                        </div>
                        <div className="text-sm font-semibold text-zinc-100 truncate">{b.title}</div>
                        {b.risk_headline && <div className="text-xs text-zinc-400 mt-0.5 truncate">{b.risk_headline}</div>}
                      </div>
                      <ChevronRight className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </div>
                    {isExpanded && (
                      <div className="border-t border-zinc-800 px-5 py-4 space-y-4">
                        {/* Key metrics */}
                        {Object.keys(metrics).length > 0 && (
                          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                            {Object.entries(metrics).map(([k, v]) => (
                              <div key={k} className="bg-zinc-900 rounded-lg p-2 text-center">
                                <div className="text-sm font-bold text-zinc-200">{String(v)}</div>
                                <div className="text-xs text-zinc-400 mt-0.5 capitalize">{k.replace(/_/g,' ')}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Sections */}
                        {sections.sort((a,b) => a.priority - b.priority).map(s => (
                          <div key={s.title} className="space-y-1">
                            <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">{s.title}</div>
                            <div className="text-sm text-zinc-300 whitespace-pre-line leading-relaxed">{s.content}</div>
                          </div>
                        ))}
                        {/* Action items */}
                        {actions.length > 0 && (
                          <div className="space-y-1">
                            <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Action Items</div>
                            <ul className="space-y-1">
                              {actions.map((a, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                                  <Target className="w-3.5 h-3.5 text-violet-400 shrink-0 mt-0.5" />
                                  {a}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      )}

      {/* ── Phase 6: Collaboration ───────────────────────────────────────── */}
      {tab === 'collab' && (
        <motion.div key="collab" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {/* Tasks panel */}
          <div className="glass-panel rounded-2xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-semibold text-zinc-200">Intelligence Tasks</span>
              <span className="ml-auto text-xs text-zinc-400">{intelTasks.filter(t => t.status === 'open' || t.status === 'in_progress').length} open</span>
            </div>
            {/* Create task form */}
            <div className="bg-zinc-900/60 rounded-xl p-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Task title *" value={taskForm.title}
                  onChange={e => setTaskForm(f => ({ ...f, title: e.target.value }))}
                  className="col-span-2 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500" />
                <input placeholder="Assignee" value={taskForm.assignee}
                  onChange={e => setTaskForm(f => ({ ...f, assignee: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500" />
                <input placeholder="Due in days" type="number" value={taskForm.due_days}
                  onChange={e => setTaskForm(f => ({ ...f, due_days: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500" />
                <select value={taskForm.priority} onChange={e => setTaskForm(f => ({ ...f, priority: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-300">
                  {['LOW','MEDIUM','HIGH','CRITICAL'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <textarea placeholder="Description (optional)" value={taskForm.description}
                  onChange={e => setTaskForm(f => ({ ...f, description: e.target.value }))}
                  rows={1}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500 resize-none" />
              </div>
              <button onClick={handleAddTask} disabled={taskSubmitting || !taskForm.title.trim() || !selectedIndustry}
                className="px-3 py-1.5 rounded-lg bg-amber-600/80 hover:bg-amber-500 text-white text-xs font-medium disabled:opacity-40 transition-all flex items-center gap-1.5">
                {taskSubmitting ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                Add Task
              </button>
            </div>
            {/* Task list */}
            {intelTasks.length === 0 ? (
              <div className="text-zinc-400 text-sm text-center py-4">No tasks yet.</div>
            ) : (
              <div className="space-y-2">
                {intelTasks.map(t => {
                  const pri_color = t.priority === 'CRITICAL' ? 'text-rose-400' : t.priority === 'HIGH' ? 'text-amber-400' : t.priority === 'MEDIUM' ? 'text-yellow-400' : 'text-zinc-400';
                  const done = t.status === 'done' || t.status === 'cancelled';
                  return (
                    <div key={t.id} className={`bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 flex items-start gap-3 ${done ? 'opacity-50' : ''}`}>
                      <button onClick={() => handleTaskStatus(t.id, done ? 'open' : 'done')}
                        className={`mt-0.5 w-4 h-4 rounded border shrink-0 flex items-center justify-center transition-all ${done ? 'bg-emerald-600 border-emerald-500' : 'border-zinc-600 hover:border-emerald-500'}`}>
                        {done && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-bold uppercase ${pri_color}`}>{t.priority}</span>
                          <span className={`text-sm font-medium ${done ? 'line-through text-zinc-400' : 'text-zinc-200'}`}>{t.title}</span>
                        </div>
                        {t.description && <div className="text-xs text-zinc-400 mt-0.5 truncate">{t.description}</div>}
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          {t.assignee && <span className="text-xs text-zinc-400">@{t.assignee}</span>}
                          {t.due_date && <span className="flex items-center gap-1 text-xs text-zinc-400"><Clock className="w-3 h-3" /> {new Date(t.due_date).toLocaleDateString()}</span>}
                          <span className="text-xs text-zinc-400 capitalize">{t.status.replace('_',' ')}</span>
                        </div>
                      </div>
                      {!done && (
                        <button onClick={() => handleTaskStatus(t.id, 'in_progress')}
                          className="text-xs px-2 py-1 rounded bg-zinc-800 text-zinc-400 hover:text-zinc-200 transition-all shrink-0">
                          In Progress
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Analyst Notes panel */}
          <div className="glass-panel rounded-2xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-semibold text-zinc-200">Analyst Notes</span>
              <button onClick={() => selectedIndustry && fetchPhase6Data(selectedIndustry)}
                className="ml-auto p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-400 transition-all">
                <RefreshCw className={`w-3.5 h-3.5 ${phase6Loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            {/* Add note form */}
            <div className="bg-zinc-900/60 rounded-xl p-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Your name *" value={noteForm.author}
                  onChange={e => setNoteForm(f => ({ ...f, author: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500" />
                <input placeholder="Tags (comma-separated)" value={noteForm.tags}
                  onChange={e => setNoteForm(f => ({ ...f, tags: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500" />
              </div>
              <textarea placeholder="Write your analysis note…" value={noteForm.note}
                onChange={e => setNoteForm(f => ({ ...f, note: e.target.value }))}
                rows={3}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-zinc-200 placeholder-zinc-500 resize-none" />
              <button onClick={handleAddNote}
                disabled={noteSubmitting || !noteForm.note.trim() || !noteForm.author.trim() || !selectedIndustry}
                className="px-3 py-1.5 rounded-lg bg-violet-600/80 hover:bg-violet-500 text-white text-xs font-medium disabled:opacity-40 transition-all flex items-center gap-1.5">
                {noteSubmitting ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                Add Note
              </button>
            </div>
            {/* Notes list */}
            {analystNotes.length === 0 ? (
              <div className="text-zinc-400 text-sm text-center py-4">No analyst notes yet.</div>
            ) : (
              <div className="space-y-2">
                {analystNotes.map(n => {
                  const tags: string[] = (() => { try { return JSON.parse(n.tags || '[]'); } catch { return []; } })();
                  return (
                    <div key={n.id} className={`bg-zinc-900 border rounded-xl px-4 py-3 ${n.is_pinned ? 'border-violet-500/40' : 'border-zinc-800'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {n.is_pinned && <span className="text-xs text-violet-400 font-semibold">Pinned</span>}
                            <span className="text-xs font-medium text-zinc-300">{n.author}</span>
                            <span className="text-xs text-zinc-400">{fmtDate(n.created_at)}</span>
                            {tags.map(t => <span key={t} className="text-xs bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">{t}</span>)}
                          </div>
                          <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{n.note}</div>
                        </div>
                        <button onClick={() => handleDeleteNote(n.id)}
                          className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 hover:text-red-400 transition-all shrink-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}

